import React, { useEffect, useMemo, useState } from "react";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { db } from "../firebase";
import { parseIndexMonth, fetchStatssaIndices, mapResultToIndexValues, STATSSA_SERIES_KEYS, STATSSA_SERIES_LABELS, MATERIAL_SERIES_OPTIONS, MATERIAL_SERIES_DEFAULT_KEY } from "../utils/statssaIndices";

const SECTION_STYLES = {
  page: {
    display: "grid",
    gap: 22,
  },
  sectionCard: {
    background: "#ffffff",
    border: "1px solid #dbe7f5",
    borderRadius: 22,
    padding: 22,
    boxShadow: "0 16px 40px rgba(15, 23, 42, 0.06)",
    display: "grid",
    gap: 18,
  },
  sectionHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 16,
    flexWrap: "wrap",
  },
  sectionTitle: {
    margin: 0,
    fontSize: 22,
    fontWeight: 800,
    color: "#0f172a",
    letterSpacing: "-0.03em",
  },
  sectionSubtitle: {
    margin: "6px 0 0 0",
    fontSize: 14,
    color: "#64748b",
    lineHeight: 1.6,
  },
  buttonRow: {
    display: "flex",
    gap: 10,
    flexWrap: "wrap",
  },
  primaryButton: {
    background: "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)",
    color: "#fff",
    border: "none",
    borderRadius: 12,
    padding: "12px 16px",
    fontWeight: 700,
    cursor: "pointer",
    boxShadow: "0 14px 28px rgba(37, 99, 235, 0.28)",
  },
  secondaryButton: {
    background: "#ffffff",
    color: "#0f172a",
    border: "1px solid #cbd5e1",
    borderRadius: 12,
    padding: "12px 16px",
    fontWeight: 700,
    cursor: "pointer",
  },
  compactButton: {
    background: "#ffffff",
    color: "#0f172a",
    border: "1px solid #cbd5e1",
    borderRadius: 10,
    padding: "8px 10px",
    fontWeight: 700,
    cursor: "pointer",
  },
  dangerButton: {
    background: "linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)",
    color: "#fff",
    border: "none",
    borderRadius: 10,
    padding: "8px 10px",
    fontWeight: 700,
    cursor: "pointer",
  },
  kpiGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: 14,
  },
  kpiCard: {
    border: "1px solid #dbe7f5",
    borderRadius: 16,
    background: "#f8fbff",
    padding: 16,
    display: "grid",
    gap: 6,
  },
  kpiLabel: {
    margin: 0,
    fontSize: 12,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    fontWeight: 800,
    color: "#64748b",
  },
  kpiValue: {
    margin: 0,
    fontSize: 20,
    fontWeight: 800,
    letterSpacing: "-0.03em",
    color: "#0f172a",
  },
  tableWrap: {
    overflowX: "auto",
    border: "1px solid #dbe7f5",
    borderRadius: 18,
  },
  stickyCell: {
    position: "sticky",
    left: 0,
    background: "#f8fbff",
    zIndex: 2,
    minWidth: 260,
    borderRight: "1px solid #dbe7f5",
  },
  rowLabel: {
    fontWeight: 700,
    color: "#0f172a",
  },
  inputCell: {
    minWidth: 170,
  },
  readonlyCell: {
    minWidth: 170,
    background: "#f8fbff",
    color: "#0f172a",
    fontWeight: 700,
  },
  muted: {
    color: "#64748b",
    fontSize: 12,
  },
};

// ─── Orange "provisional" input style ────────────────────────────────────────
const PROVISIONAL_INDEX_STYLE = {
  background: "#fff7ed",
  border: "1.5px solid #f97316",
  borderRadius: 6,
  color: "#92400e",
  fontWeight: 700,
  outline: "none",
};

const CPA_CONSTANTS = {
  labourWeight: 0.3,
  plantWeight: 0.3,
  materialWeight: 0.3,
  fuelWeight: 0.1,
  nonAdjustablePortion: 0.1,
  baseLabourIndex: 90.8213,
  basePlantIndex: 91.6163,
  baseMaterialIndex: 88.6819,
  baseFuelIndex: 116.9507,
  defaultBaseDateYear: "2021",
  defaultBaseSteel: "9299",
};

const GCC_CPA_BASE_INDEX_DEFINITION = {
  key: "gcc",
  rowIndexLabels: ["labourIndex", "plantIndex", "materialIndex", "fuelIndex"],
  baseIndexLabels: {
    labourIndex: "Lo - Labour",
    plantIndex: "Po - Plant",
    materialIndex: "Mo - Materials",
    fuelIndex: "Fo - Fuel",
  },
  defaultBaseIndices: {
    labourIndex: CPA_CONSTANTS.baseLabourIndex,
    plantIndex: CPA_CONSTANTS.basePlantIndex,
    materialIndex: CPA_CONSTANTS.baseMaterialIndex,
    fuelIndex: CPA_CONSTANTS.baseFuelIndex,
  },
};

export const FIDIC_CPA_TABLES = [
  {
    key: "mechanicalPlantMaterials",
    title: "Mechanical Plant and Materials",
    baseMonth: "MAY",
    baseYear: "2023",
    formula: "A = a + b*Ln/Lo + c*Mn/Mo + d*Sn/So + e*SSn/SSo - 1",
    factorKeys: ["a", "b", "c", "d", "e"],
    defaultFactors: { a: 0.1, b: 0.15, c: 0.35, d: 0.25, e: 0.15 },
    baseIndexLabels: { Ln: "Lo", Mn: "Mo", Sn: "So", SSn: "SSo" },
    defaultBaseIndices: { Ln: "", Mn: 98.61, Sn: 263.9, SSn: 240 },
    rowIndexLabels: ["Ln", "Mn", "Sn", "SSn"],
  },
  {
    key: "electricalPlantMaterials",
    title: "Electrical Plant and Materials",
    baseMonth: "MAY",
    baseYear: "2023",
    formula: "A = a + b*Ln/Lo + c*En/Eo + d*CRn/CRo + e*Cn/Co - 1",
    factorKeys: ["a", "b", "c", "d", "e"],
    defaultFactors: { a: 0.1, b: 0.1, c: 0.3, d: 0.2, e: 0.3 },
    baseIndexLabels: { Ln: "Lo", En: "Eo", CRn: "CRo", Cn: "Co" },
    defaultBaseIndices: { Ln: "", En: 96.2, CRn: 218.8, Cn: 172150.03 },
    rowIndexLabels: ["Ln", "En", "CRn", "Cn"],
  },
  {
    key: "siteInstallationGeneral",
    title: "Site Installation and General Items",
    baseMonth: "MAY",
    baseYear: "2023",
    formula: "A = a + b*Ln/Lo - 1",
    note: "No CPA for Performance Guarantee and Insurances",
    factorKeys: ["a", "b"],
    defaultFactors: { a: 0.1, b: 0.9 },
    baseIndexLabels: { Ln: "Lo" },
    defaultBaseIndices: { Ln: "" },
    rowIndexLabels: ["Ln"],
  },
  {
    key: "earthworksGeneralCivil",
    title: "CPA: Earthworks & General Civil Works",
    baseMonth: "MAY",
    baseYear: "2023",
    formula: "f = (1-x)*(a*Lt/Lo + b*Pt/Po + c*Mt/Mo + d*Ft/Fo - 1)",
    nonAdjustableKey: "x",
    factorRounding: "roundUp4",
    factorDisplayDecimals: 4,
    useRoundedFactorForCalculation: true,
    defaultNonAdjustable: 0.1,
    factorKeys: ["a", "b", "c", "d"],
    defaultFactors: { a: 0.15, b: 0.5, c: 0.2, d: 0.15 },
    baseIndexLabels: { Lt: "Lo", Pt: "Po", Mt: "Mo", Ft: "Fo" },
    oldBaseIndices: { Lt: 109.6, Pt: 115.7, Mt: 110.5, Ft: 164.6 },
    dec21Indices: { Lt: 112.7, Pt: 121.1, Mt: 108.9, Ft: 177.6 },
    dec23Indices: { Lt: 112.7, Pt: 100, Mt: 100, Ft: 100 },
    rowIndexLabels: ["Lt", "Pt", "Mt", "Ft"],
  },
  {
    key: "concreteWorks",
    title: "CPA: Concrete Works",
    baseMonth: "MAY",
    baseYear: "2023",
    formula: "f = (1-x)*(a*Lt/Lo + b*Pt/Po + c*Mt/Mo + d*Ft/Fo - 1)",
    nonAdjustableKey: "x",
    factorRounding: "none",
    factorDisplayDecimals: 6,
    useRoundedFactorForCalculation: false,
    defaultNonAdjustable: 0.1,
    factorKeys: ["a", "b", "c", "d"],
    defaultFactors: { a: 0.25, b: 0.15, c: 0.55, d: 0.05 },
    baseIndexLabels: { Lt: "Lo", Pt: "Po", Mt: "Mo", Ft: "Fo" },
    oldBaseIndices: { Lt: 109.6, Pt: 115.7, Mt: 110.5, Ft: 164.6 },
    dec21Indices: { Lt: 112.7, Pt: 121.1, Mt: 108.9, Ft: 177.6 },
    dec23Indices: { Lt: 112.7, Pt: 100, Mt: 100, Ft: 100 },
    defaultBaseIndices: { Lt: "", Pt: "", Mt: roundUpTo4((110.5 * 100) / 108.9), Ft: roundUpTo4((164.6 * 100) / 177.6) },
    rowIndexLabels: ["Lt", "Pt", "Mt", "Ft"],
  },
];


function getRebasedBaseIndex(tableDefinition, label) {
  const oldBase = numberOrZero(tableDefinition.oldBaseIndices?.[label]);
  const dec21 = numberOrZero(tableDefinition.dec21Indices?.[label]);
  const dec23 = numberOrZero(tableDefinition.dec23Indices?.[label]);

  if (!oldBase || !dec21) return null;
  return roundUpTo4((oldBase * dec23) / dec21);
}

// --- Base Index Set helpers ---
function createDefaultBaseIndexSet(tableDefinition, index = 0, existingBaseIndices = {}) {
  const baseIndices = (tableDefinition.rowIndexLabels || []).reduce((acc, label) => {
    if (existingBaseIndices && existingBaseIndices[label] !== undefined) {
      acc[label] = existingBaseIndices[label];
      return acc;
    }

    if (tableDefinition.defaultBaseIndices && tableDefinition.defaultBaseIndices[label] !== undefined) {
      acc[label] = tableDefinition.defaultBaseIndices[label];
      return acc;
    }

    const rebasedIndex = getRebasedBaseIndex(tableDefinition, label);
    acc[label] = rebasedIndex ?? "";
    return acc;
  }, {});

  return {
    id: `base-set-${index + 1}`,
    label: `Base Set ${index + 1}`,
    revisedMonth: "",
    baseIndices,
  };
}

function normaliseBaseIndexSets(tableDefinition, tableState = {}) {
  const existingSets = Array.isArray(tableState.baseIndexSets) ? tableState.baseIndexSets : [];

  if (existingSets.length) {
    return existingSets.map((set, index) => ({
      id: set.id || `base-set-${index + 1}`,
      label: set.label || `Base Set ${index + 1}`,
      revisedMonth: set.revisedMonth || "",
      baseIndices: (tableDefinition.rowIndexLabels || []).reduce((acc, label) => {
        acc[label] =
          set.baseIndices?.[label] ??
          tableState.baseIndices?.[label] ??
          tableDefinition.defaultBaseIndices?.[label] ??
          getRebasedBaseIndex(tableDefinition, label) ??
          "";
        return acc;
      }, {}),
    }));
  }

  return [createDefaultBaseIndexSet(tableDefinition, 0, tableState.baseIndices || {})];
}

function getBaseIndexSetForRow(tableDefinition, tableState = {}, row = {}) {
  const baseIndexSets = normaliseBaseIndexSets(tableDefinition, tableState);
  return (
    baseIndexSets.find((set) => String(set.id) === String(row.baseIndexSetId)) ||
    baseIndexSets[0]
  );
}

function normaliseGccBaseIndexSets(columns = []) {
  const sourceColumn =
    (columns || []).find((column) => Array.isArray(column.baseIndexSets) && column.baseIndexSets.length) ||
    (columns || [])[0] ||
    {};

  return normaliseBaseIndexSets(GCC_CPA_BASE_INDEX_DEFINITION, sourceColumn);
}

function getGccBaseIndexSetForColumn(column = {}) {
  const baseIndexSets = normaliseBaseIndexSets(GCC_CPA_BASE_INDEX_DEFINITION, column);
  return (
    baseIndexSets.find((set) => String(set.id) === String(column.baseIndexSetId)) ||
    baseIndexSets[0]
  );
}

// ─── CHANGE 1: createEmptyFidicRow now accepts previousRow and copies its index values ───
function createEmptyFidicRow(index = 0, tableDefinition, previousRow = null) {
  const indexValues = (tableDefinition?.rowIndexLabels || []).reduce((acc, label) => {
    // Copy previous row's index values so the user has a starting point for the new month.
    // Cells will be highlighted orange (provisional) until the row status is changed to "F".
    acc[label] = previousRow?.indexValues?.[label] ?? "";
    return acc;
  }, {});

  return {
    id: `FIDIC-CPA-ROW-${Date.now()}-${index}`,
    certificateNo: String(index + 1),
    certificateDate: "",
    indexMonth: "",
    indexStatus: "UL",
    baseIndexSetId: previousRow?.baseIndexSetId || "base-set-1",
workValue: "",

lockedProvisionalCpaAmount: "",

notes: "",

indexValues,
    // Flag: copied from previous row — used to drive the orange highlight.
    // Persists until status flips to "F".
    _copiedFromPrevious: previousRow != null,
  };
}

function createFidicTableState(tableDefinition) {
  const calculatedBaseIndices = tableDefinition.rowIndexLabels.reduce((acc, label) => {
    if (tableDefinition.defaultBaseIndices && tableDefinition.defaultBaseIndices[label] !== undefined) {
      acc[label] = tableDefinition.defaultBaseIndices[label];
      return acc;
    }

    const oldBase = numberOrZero(tableDefinition.oldBaseIndices?.[label]);
    const dec21 = numberOrZero(tableDefinition.dec21Indices?.[label]);
    const dec23 = numberOrZero(tableDefinition.dec23Indices?.[label]);
    acc[label] = dec21 ? roundUpTo4((oldBase * dec23) / dec21) : "";
    return acc;
  }, {});

  return {
    key: tableDefinition.key,
    enabled: true,
    baseMonth: tableDefinition.baseMonth,
    baseYear: tableDefinition.baseYear,
    factors: { ...tableDefinition.defaultFactors },
    nonAdjustable: tableDefinition.defaultNonAdjustable ?? "",
    baseIndices: calculatedBaseIndices,
    baseIndexSets: [createDefaultBaseIndexSet(tableDefinition, 0, calculatedBaseIndices)],
    rows: [createEmptyFidicRow(0, tableDefinition, null)],
  };
}

export function createDefaultFidicCpaState() {
  return FIDIC_CPA_TABLES.reduce((acc, tableDefinition) => {
    acc[tableDefinition.key] = createFidicTableState(tableDefinition);
    return acc;
  }, {});
}

function normaliseFidicCpaState(fidicCpaTables = {}) {
  const defaults = createDefaultFidicCpaState();

  return FIDIC_CPA_TABLES.reduce((acc, tableDefinition) => {
    const existing = fidicCpaTables?.[tableDefinition.key] || {};
    acc[tableDefinition.key] = {
      ...defaults[tableDefinition.key],
      ...existing,
      factors: {
        ...defaults[tableDefinition.key].factors,
        ...(existing.factors || {}),
      },
      baseIndices: {
        ...defaults[tableDefinition.key].baseIndices,
        ...(existing.baseIndices || {}),
      },
      baseIndexSets: normaliseBaseIndexSets(tableDefinition, {
        ...defaults[tableDefinition.key],
        ...existing,
      }),
      rows: (existing.rows?.length ? existing.rows : defaults[tableDefinition.key].rows).map((row) => ({
        ...row,
        baseIndexSetId: row.baseIndexSetId || "base-set-1",
      })),
    };
    return acc;
  }, {});
}

function calculateFidicFactor(tableDefinition, tableState, row) {
  const factors = tableDefinition.factorKeys.reduce((acc, key) => {
    acc[key] = numberOrZero(tableState.factors?.[key]);
    return acc;
  }, {});

  const selectedBaseSet = getBaseIndexSetForRow(tableDefinition, tableState, row);

  const ratio = (label) => {
    const storedDenominator = numberOrZero(selectedBaseSet?.baseIndices?.[label]);
    const legacyDenominator = numberOrZero(tableState.baseIndices?.[label]);
    const rebasedDenominator = getRebasedBaseIndex(tableDefinition, label);
    const denominator = storedDenominator || legacyDenominator || rebasedDenominator;

    if (!denominator) return 0;
    return numberOrZero(row.indexValues?.[label]) / denominator;
  };

  if (tableDefinition.key === "mechanicalPlantMaterials") {
    return factors.a + factors.b * ratio("Ln") + factors.c * ratio("Mn") + factors.d * ratio("Sn") + factors.e * ratio("SSn") - 1;
  }

  if (tableDefinition.key === "electricalPlantMaterials") {
    return factors.a + factors.b * ratio("Ln") + factors.c * ratio("En") + factors.d * ratio("CRn") + factors.e * ratio("Cn") - 1;
  }

  if (tableDefinition.key === "siteInstallationGeneral") {
    return factors.a + factors.b * ratio("Ln") - 1;
  }

  const nonAdjustable = numberOrZero(tableState.nonAdjustable);
  return (1 - nonAdjustable) * (factors.a * ratio("Lt") + factors.b * ratio("Pt") + factors.c * ratio("Mt") + factors.d * ratio("Ft") - 1);
}

function getRoundedFidicFactor(rawFactor, tableDefinition) {
  if (tableDefinition.factorRounding === "roundUp4") {
    return roundUpTo4(rawFactor);
  }

  if (tableDefinition.factorRounding === "roundTo4") {
    return roundTo4(rawFactor);
  }

  if (tableDefinition.factorRounding === "roundTo6") {
    return roundTo(rawFactor, 6);
  }

  return rawFactor;
}

function getFidicCalculationFactor(rawFactor, tableDefinition) {
  const roundedFactor = getRoundedFidicFactor(rawFactor, tableDefinition);
  return tableDefinition.useRoundedFactorForCalculation ? roundedFactor : rawFactor;
}

function calculateFidicTable(tableDefinition, tableState) {
  const rows = (tableState.rows || []).map((row) => {
    const rawFactor = calculateFidicFactor(tableDefinition, tableState, row);
    const displayedFactor = getRoundedFidicFactor(rawFactor, tableDefinition);
    const calculationFactor = getFidicCalculationFactor(rawFactor, tableDefinition);
    const workValue = numberOrZero(row.workValue);
    const escalation = roundTo(workValue * calculationFactor);

    const rowStatus = String(row.indexStatus || "UL").toUpperCase();
    const lockedProvisionalCpaAmount = numberOrZero(row.lockedProvisionalCpaAmount);

    const provisionalCpaAmount =
      rowStatus === "UL"
        ? (lockedProvisionalCpaAmount || escalation)
        : 0;

    const finalCpaAmount =
      rowStatus === "L" ? escalation : 0;

    const carriedCpaAmount =
      rowStatus === "L" ? finalCpaAmount : provisionalCpaAmount;

    return {
      ...row,
      computed: {
        factor: displayedFactor,
        rawFactor,
        calculationFactor,
        workValue,
        escalation,
        provisionalCpaAmount,
        finalCpaAmount,
        carriedCpaAmount,
        lockedProvisionalCpaAmount,
      },
    };
  });

  return {
    ...tableState,
    rows,
    totalEscalation: roundTo(
      rows.reduce((sum, row) => sum + numberOrZero(row.computed?.carriedCpaAmount), 0)
    ),
    totalProvisionalCpa: roundTo(
      rows.reduce((sum, row) => sum + numberOrZero(row.computed?.provisionalCpaAmount), 0)
    ),
    totalFinalCpa: roundTo(
      rows.reduce((sum, row) => sum + numberOrZero(row.computed?.finalCpaAmount), 0)
    ),
    totalRawEscalation: roundTo(
      rows.reduce((sum, row) => sum + numberOrZero(row.computed?.escalation), 0)
    ),
  };
}

export function calculateFidicCpaTotals(fidicCpaTables = {}) {
  const normalised = normaliseFidicCpaState(fidicCpaTables);
  const computedTables = FIDIC_CPA_TABLES.reduce((acc, tableDefinition) => {
    acc[tableDefinition.key] = calculateFidicTable(tableDefinition, normalised[tableDefinition.key]);
    return acc;
  }, {});

  // Per table: ruling CPA = for each cert, prefer Final row over Provisional
  const getRulingTotal = (computedTable) => {
    if (!computedTable || computedTable.enabled === false) return 0;
    const rulingByCert = {};
    (computedTable.rows || []).forEach((row) => {
      const cert = String(row.certificateNo || "").trim();
      if (!cert) return;
      const isFinal = String(row.indexStatus || "UL").toUpperCase() === "L";
      if (!rulingByCert[cert] || isFinal) rulingByCert[cert] = row;
    });
    return roundTo(Object.values(rulingByCert).reduce((s, r) => s + numberOrZero(r.computed?.carriedCpaAmount), 0));
  };

  const enabledTables = FIDIC_CPA_TABLES.filter((td) => computedTables[td.key]?.enabled !== false);
  const combinedTotalCpa = roundTo(
    enabledTables.reduce((sum, td) => sum + getRulingTotal(computedTables[td.key]), 0)
  );

  // Attach rulingTotal to each computedTable for KPI tiles
  FIDIC_CPA_TABLES.forEach((td) => {
    if (computedTables[td.key]) computedTables[td.key].totalEscalation = getRulingTotal(computedTables[td.key]);
  });

  return { computedTables, combinedTotalCpa };
}

export function buildFidicCpaByCertificateNo(fidicCpaTables = {}) {
  const fidicTotals = calculateFidicCpaTotals(fidicCpaTables);

  const tableTitles = FIDIC_CPA_TABLES.reduce((acc, td) => { acc[td.key] = td.title; return acc; }, {});

  // Step 1: per table, per cert — ruling row (Final beats Provisional)
  const perTablePerCert = {};

  FIDIC_CPA_TABLES.forEach((tableDefinition) => {
    const computedTable = fidicTotals.computedTables?.[tableDefinition.key];
    if (!computedTable || computedTable.enabled === false) return;

    const rulingByCert = {};
    (computedTable.rows || []).forEach((row) => {
      const cert = String(row.certificateNo || "").trim();
      if (!cert) return;
      const isFinal = String(row.indexStatus || "UL").toUpperCase() === "L";
      if (!rulingByCert[cert] || isFinal) rulingByCert[cert] = row;
    });

    const provisionalByCert = {};
    (computedTable.rows || []).forEach((row) => {
      const cert = String(row.certificateNo || "").trim();
      if (!cert) return;
      const prov = numberOrZero(row.computed?.provisionalCpaAmount);
      if (!provisionalByCert[cert] || prov > numberOrZero(provisionalByCert[cert].computed?.provisionalCpaAmount)) {
        provisionalByCert[cert] = row;
      }
    });

    Object.keys(rulingByCert).forEach((cert) => {
      if (!perTablePerCert[cert]) perTablePerCert[cert] = {};
      const rulingRow = rulingByCert[cert];
      const provRow = provisionalByCert[cert];
      const isFinal = String(rulingRow.indexStatus || "UL").toUpperCase() === "L";
      perTablePerCert[cert][tableDefinition.key] = {
        tableTitle: tableTitles[tableDefinition.key] || tableDefinition.key,
        rulingAmount: numberOrZero(rulingRow.computed?.carriedCpaAmount),
        provisionalAmount: numberOrZero(provRow?.computed?.provisionalCpaAmount),
        isFinal,
      };
    });
  });

  const certNos = Object.keys(perTablePerCert).sort((a, b) =>
    String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: "base" })
  );

  // certHistory: ordered array of { certNo, perTable: { tableKey: { rulingAmount, provisionalAmount, isFinal, tableTitle } } }
  // Used by the tooltip to render one line per cert per table.
  const certHistory = [];
  const runningRulingPerTable = {};
  let runningRulingCumulative = 0;
  const result = {};

  certNos.forEach((cert) => {
    const tables = perTablePerCert[cert];
    let rulingThisCert = 0;
    let provisionalThisCert = 0;
    const tableBreakdown = [];
    const prevRunningPerTable = { ...runningRulingPerTable };

    Object.entries(tables).forEach(([tableKey, data]) => {
      rulingThisCert = roundTo(rulingThisCert + data.rulingAmount);
      provisionalThisCert = roundTo(provisionalThisCert + data.provisionalAmount);
      tableBreakdown.push({
        tableKey,
        tableTitle: data.tableTitle,
        rulingAmount: data.rulingAmount,
        provisionalAmount: data.provisionalAmount,
        isFinal: data.isFinal,
        prevCumulativeRuling: numberOrZero(prevRunningPerTable[tableKey]),
      });
      runningRulingPerTable[tableKey] = roundTo(
        numberOrZero(runningRulingPerTable[tableKey]) + data.rulingAmount
      );
    });

    runningRulingCumulative = roundTo(runningRulingCumulative + rulingThisCert);

    // Record this cert in the ordered history for tooltip use
    certHistory.push({
      certNo: cert,
      rulingThisCert,
      provisionalThisCert,
      perTable: Object.fromEntries(
        Object.entries(tables).map(([tableKey, data]) => [tableKey, { ...data }])
      ),
    });

    const entry = {
      certificateNo: cert,
      rulingCpaThisCertificate: rulingThisCert,
      provisionalCpaThisCertificate: provisionalThisCert,
      rulingCpaCumulative: runningRulingCumulative,
      cpaThisCertificate: rulingThisCert,
      cpaCertifiedValue: rulingThisCert,
      tableBreakdown,
      certHistory: certHistory.map(h => ({ ...h })),
    };

    // Store under multiple key aliases so "001", "1", "01" all resolve correctly
    result[cert] = entry;
    const numericKey = String(parseInt(cert, 10));
    if (numericKey !== cert) result[numericKey] = entry;
    const paddedKey3 = cert.padStart(3, "0");
    if (paddedKey3 !== cert) result[paddedKey3] = entry;
  });

  return result;
}

export function calculateLatestCpaForContract(contractType, gccCpaColumns = [], fidicCpaTables = {}) {
  if (String(contractType || "GCC").toUpperCase() === "FIDIC") {
    return calculateFidicCpaTotals(fidicCpaTables).combinedTotalCpa;
  }

  const computedColumns = buildComputedColumns(gccCpaColumns);
  return computedColumns.length
    ? numberOrZero(computedColumns[computedColumns.length - 1]?.computed?.totalCumulativeCpa)
    : 0;
}

function numberOrZero(value) {
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  if (typeof value === "string") {
    const cleaned = value.replace(/[^0-9.-]/g, "");
    if (!cleaned) return 0;
    const parsed = Number(cleaned);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function roundTo(value, decimals = 2) {
  const factor = 10 ** decimals;
  return Math.round((numberOrZero(value) + Number.EPSILON) * factor) / factor;
}

function roundTo4(value) {
  return roundTo(value, 4);
}

function roundUpTo(value, decimals = 2) {
  const factor = 10 ** decimals;
  const numeric = numberOrZero(value);
  return Math.ceil((numeric - Number.EPSILON) * factor) / factor;
}

function roundUpTo4(value) {
  return roundUpTo(value, 4);
}

function formatAmount(value) {
  return `R ${numberOrZero(value).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function formatIndex(value) {
  const numeric = numberOrZero(value);
  return numeric.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  });
}

function formatFactor(value, decimals = 4) {
  const numeric = numberOrZero(value);
  return numeric.toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

export function createEmptyCpaColumn(index = 0, previousColumn = null, baseIndexSets = null) {
  const normalisedBaseIndexSets =
    Array.isArray(baseIndexSets) && baseIndexSets.length
      ? baseIndexSets
      : normaliseGccBaseIndexSets(previousColumn ? [previousColumn] : []);

  return {
    id: `CPA-${Date.now()}-${index}`,
    certificateNo: String(index + 1).padStart(3, "0"),
    certificateDate: "",
    certificateMonth: "",
    indexStatus: "UL",
    baseIndexSetId: previousColumn?.baseIndexSetId || normalisedBaseIndexSets[0]?.id || "base-set-1",
    baseIndexSets: normalisedBaseIndexSets,
    baseDateYear: CPA_CONSTANTS.defaultBaseDateYear,
    baseDate: "",
    periodNo: String(index + 1),
    labourIndex: "",
    plantIndex: "",
    materialIndex: "",
    fuelIndex: "",
    totalScheduledItems: "",
    addMos: "",
    addVariations: "",
    subtractNonEscalatable: "",
    subtractDayworks: "",
    baseSteel: CPA_CONSTANTS.defaultBaseSteel,
    steelRate: "",
  steelVolume: "",

lockedProvisionalCpaAmount: "",

notes: "",
  };
}

function calculateCpaColumn(column, previousColumn) {
  const selectedBaseSet = getGccBaseIndexSetForColumn(column);
  const baseLabourIndex =
    numberOrZero(selectedBaseSet?.baseIndices?.labourIndex) || CPA_CONSTANTS.baseLabourIndex;
  const basePlantIndex =
    numberOrZero(selectedBaseSet?.baseIndices?.plantIndex) || CPA_CONSTANTS.basePlantIndex;
  const baseMaterialIndex =
    numberOrZero(selectedBaseSet?.baseIndices?.materialIndex) || CPA_CONSTANTS.baseMaterialIndex;
  const baseFuelIndex =
    numberOrZero(selectedBaseSet?.baseIndices?.fuelIndex) || CPA_CONSTANTS.baseFuelIndex;
  const labourIndex = numberOrZero(column.labourIndex);
  const plantIndex = numberOrZero(column.plantIndex);
  const materialIndex = numberOrZero(column.materialIndex);
  const fuelIndex = numberOrZero(column.fuelIndex);

  const cpaFactor = roundTo4(
    (1 - CPA_CONSTANTS.nonAdjustablePortion) *
      ((
        (CPA_CONSTANTS.labourWeight * labourIndex) / baseLabourIndex +
        (CPA_CONSTANTS.plantWeight * plantIndex) / basePlantIndex +
        (CPA_CONSTANTS.materialWeight * materialIndex) / baseMaterialIndex +
        (CPA_CONSTANTS.fuelWeight * fuelIndex) / baseFuelIndex
      ) - 1)
  );

  const totalScheduledItems = numberOrZero(column.totalScheduledItems);
  const addMos = numberOrZero(column.addMos);
  const addVariations = numberOrZero(column.addVariations);
  const subtractNonEscalatable = numberOrZero(column.subtractNonEscalatable);
  const subtractDayworks = numberOrZero(column.subtractDayworks);

  const subtotal = roundTo(
    totalScheduledItems + addMos + addVariations - subtractNonEscalatable - subtractDayworks
  );

  const previousSubtotal = numberOrZero(previousColumn?.computed?.subtotal);
  const subtractPreviousTotal = roundTo(subtotal - previousSubtotal);
  const cpaGeneral = roundTo(subtractPreviousTotal * cpaFactor);

  const baseSteel = numberOrZero(column.baseSteel || CPA_CONSTANTS.defaultBaseSteel);
  const steelRate = numberOrZero(column.steelRate);
  const steelVolume = numberOrZero(column.steelVolume);
  const specialMaterialVariation = roundTo((steelRate - baseSteel) * steelVolume);

const cpaTotal = roundTo(cpaGeneral + specialMaterialVariation);

const indexStatus = String(column.indexStatus || "UL").toUpperCase();
const lockedProvisionalCpaAmount = numberOrZero(column.lockedProvisionalCpaAmount);

const provisionalCpaAmount =
  indexStatus === "UL"
    ? (lockedProvisionalCpaAmount || cpaTotal)
    : 0;

const finalCpaAmount =
  indexStatus === "L" ? cpaTotal : 0;

const carriedCpaAmount =
  indexStatus === "L" ? finalCpaAmount : provisionalCpaAmount;

const previousCumulativeCpaTotal = numberOrZero(previousColumn?.computed?.totalCumulativeCpa);
const totalCumulativeCpa = roundTo(previousCumulativeCpaTotal + carriedCpaAmount);
const certifiedValueIncludingCpa = roundTo(carriedCpaAmount + subtractPreviousTotal);
  return {
    labourIndex,
    plantIndex,
    materialIndex,
    fuelIndex,
    baseIndexSetId: selectedBaseSet?.id || "base-set-1",
    baseIndexSetLabel: selectedBaseSet?.label || "Base Set 1",
    baseIndexRevisedMonth: selectedBaseSet?.revisedMonth || "",
    baseLabourIndex,
    basePlantIndex,
    baseMaterialIndex,
    baseFuelIndex,
    cpaFactor,
    subtotal,
    subtractPreviousTotal,
    cpaGeneral,
    baseSteel,
    steelRate,
    steelVolume,
    specialMaterialVariation,
   cpaTotal,

provisionalCpaAmount,

finalCpaAmount,

carriedCpaAmount,

lockedProvisionalCpaAmount,

previousCumulativeCpaTotal,

totalCumulativeCpa,

certifiedValueIncludingCpa,
  };
}

function buildComputedColumns(columns) {
  return (columns || []).reduce((acc, column) => {
    const previousColumn = acc.length ? acc[acc.length - 1] : null;
    const computed = calculateCpaColumn(column, previousColumn);
    acc.push({
      ...column,
      computed,
    });
    return acc;
  }, []);
}

function getMonthValue(month) {
  if (typeof month === "string") return month;
  return month?.value || month?.key || "";
}

function getMonthLabel(month) {
  if (typeof month === "string") return month;
  return month?.label || month?.value || month?.key || "";
}

function normaliseCertificateNo(value) {
  return String(value || "").trim().toLowerCase();
}

function getCertificateNoFromRecord(certificate = {}) {
  return (
    certificate.certificateNo ||
    certificate.certificateNumber ||
    certificate.paymentCertificateNo ||
    certificate.pcNo ||
    certificate.id ||
    ""
  );
}

function buildCertificateLookup(storedCertificates = []) {
  return (storedCertificates || []).reduce((acc, certificate) => {
    const certificateNo = normaliseCertificateNo(getCertificateNoFromRecord(certificate));
    if (certificateNo) acc[certificateNo] = certificate;
    return acc;
  }, {});
}

// ─── CHANGE 2: Helper to determine whether a row's index cells should be orange ───
// A row is "provisional" (orange) when its indexStatus is "P".
// The _copiedFromPrevious flag is set on newly added rows but the orange simply
// stays on as long as the status remains "P", regardless of whether the user has
// edited the values — this is the clearest signal for the QS.
function isRowProvisional(row) {
  return String(row.indexStatus || "UL").toUpperCase() === "UL";
}

const CPA_ROW_DEFINITIONS = [
  { key: "baseIndexSetLabel", label: "Selected base index set", type: "computed", kind: "text", source: "computed.baseIndexSetLabel" },
  { key: "baseIndexRevisedMonth", label: "Month base indices revised", type: "computed", kind: "text", source: "computed.baseIndexRevisedMonth" },
  { key: "baseLabourIndex", label: "Base labour index (Lo)", type: "computed", kind: "index", source: "computed.baseLabourIndex" },
  { key: "basePlantIndex", label: "Base plant index (Po)", type: "computed", kind: "index", source: "computed.basePlantIndex" },
  { key: "baseMaterialIndex", label: "Base material index (Mo)", type: "computed", kind: "index", source: "computed.baseMaterialIndex" },
  { key: "baseFuelIndex", label: "Base fuel index (Fo)", type: "computed", kind: "index", source: "computed.baseFuelIndex" },
  { key: "labourIndex", label: "Labour index (P0141 table A)-cpi headline", type: "input", kind: "index" },
  { key: "plantIndex", label: "Plant index (P0151.1 table 4)-plant equipment", type: "input", kind: "index" },
  { key: "materialIndex", label: "Material (P0151.1 table 6) roads general ex bitumen", type: "input", kind: "index" },
  { key: "fuelIndex", label: "Fuel (P0142.1 table 1) diesel", type: "input", kind: "index" },
  { key: "cpaFactor", label: "CPA Factor", type: "computed", kind: "index", source: "computed.cpaFactor" },
  { key: "totalScheduledItems", label: "Total scheduled items", type: "input", kind: "amount" },
  { key: "addMos", label: "Add 80% MOS", type: "input", kind: "amount" },
  { key: "addVariations", label: "Add variations", type: "input", kind: "amount" },
  { key: "subtractNonEscalatable", label: "Subtract non escalatable", type: "input", kind: "amount" },
  { key: "subtractDayworks", label: "subtract dayworks", type: "input", kind: "amount" },
  { key: "subtotal", label: "Subtotal", type: "computed", kind: "amount", source: "computed.subtotal" },
  { key: "subtractPreviousTotal", label: "subtract previous total", type: "computed", kind: "amount", source: "computed.subtractPreviousTotal" },
  { key: "cpaGeneral", label: "CPA general", type: "computed", kind: "amount", source: "computed.cpaGeneral" },
  { key: "baseSteel", label: "base steel", type: "input", kind: "amount" },
  { key: "steelRate", label: "Steel rate", type: "input", kind: "amount" },
  { key: "steelVolume", label: "steel volume", type: "input", kind: "quantity" },
  
{ key: "specialMaterialVariation", label: "add variation in special material", type: "computed", kind: "amount", source: "computed.specialMaterialVariation" },
{ key: "cpaTotal", label: "Live CPA Total", type: "computed", kind: "amount", source: "computed.cpaTotal" },
{ key: "provisionalCpaAmount", label: "Provisional CPA Amount", type: "computed", kind: "amount", source: "computed.provisionalCpaAmount", highlight: true },
{ key: "finalCpaAmount", label: "Final CPA Amount", type: "computed", kind: "amount", source: "computed.finalCpaAmount", highlight: true },
{ key: "carriedCpaAmount", label: "CPA Amount Carried Forward", type: "computed", kind: "amount", source: "computed.carriedCpaAmount", highlight: true },
{ key: "issuedLockedCpaThisCertificate", label: "CPA previously certified / locked", type: "computed", kind: "amount", source: "computed.issuedLockedCpaThisCertificate" },



  { key: "adjustmentFromIssuedCpa", label: "Adjustment vs issued CPA", type: "computed", kind: "amount", source: "computed.adjustmentFromIssuedCpa", highlight: true },
  { key: "previousCumulativeCpaTotal", label: "previous Cumalitive CPA total", type: "computed", kind: "amount", source: "computed.previousCumulativeCpaTotal" },
  { key: "totalCumulativeCpa", label: "total cumalative cpa", type: "computed", kind: "amount", source: "computed.totalCumulativeCpa", highlight: true },
  { key: "issuedLockedCpaCumulative", label: "Cumulative CPA previously certified / locked", type: "computed", kind: "amount", source: "computed.issuedLockedCpaCumulative" },
  { key: "cumulativeAdjustmentFromIssued", label: "Cumulative adjustment vs issued CPA", type: "computed", kind: "amount", source: "computed.cumulativeAdjustmentFromIssued", highlight: true },
  { key: "certifiedValueIncludingCpa", label: "Certified value including CPA", type: "computed", kind: "amount", source: "computed.certifiedValueIncludingCpa" },
];

function getValueFromSource(column, source) {
  if (!source) return "";
  return source.split(".").reduce((acc, key) => (acc ? acc[key] : undefined), column);
}

// ─── Modal overlay for capturing / editing a monthly CPA entry ───────────────
function CpaEntryModal({ tableDefinition, tableState, baseIndexSets, storedCertificates = [], editingEntry = null, onSave, onClose }) {
  const certificateOptions = (storedCertificates || []).map((cert) => {
    const no = cert.certificateNo || cert.certificateNumber || cert.paymentCertificateNo || cert.pcNo || cert.id || "";
    const date = cert.certificateDate || cert.date || "";
    const month = cert.certificateMonth || cert.month || "";
    return { value: String(no), label: [no, month || date].filter(Boolean).join(" — "), date, month, subtotalA: cert.subtotalA ?? cert.effectiveSubtotalA ?? "" };
  }).filter((c) => c.value);

  const firstSet = baseIndexSets[0];

  const buildForm = (entry) => {
    if (entry) {
      return {
        certificateNo: entry.certificateNo || "",
        indexMonth: entry.indexMonth || "",
        workValue: entry.workValue ?? "",
        indexStatus: entry.indexStatus || "UL",
        baseIndexSetId: entry.baseIndexSetId || firstSet?.id || "base-set-1",
        indexValues: { ...(entry.indexValues || {}) },
      };
    }
    return {
      certificateNo: "",
      indexMonth: "",
      workValue: "",
      indexStatus: "UL",
      baseIndexSetId: firstSet?.id || "base-set-1",
      indexValues: (tableDefinition.rowIndexLabels || []).reduce((acc, lbl) => { acc[lbl] = ""; return acc; }, {}),
    };
  };

  const [form, setForm] = useState(() => buildForm(editingEntry));

  // Reset form whenever the entry being edited changes
  useEffect(() => {
    setForm(buildForm(editingEntry));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingEntry?.id]);

  const set = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));
  const setIndex = (lbl, value) => setForm((prev) => ({ ...prev, indexValues: { ...prev.indexValues, [lbl]: value } }));

  // ── StatsSA auto-fill ──────────────────────────────────────────────────────
  // Which of this table's index labels StatsSA can source (default 4-series table).
  const statssaLabels = (tableDefinition.rowIndexLabels || []).filter((l) => STATSSA_SERIES_KEYS.includes(l));
  const [statssaBusy, setStatssaBusy] = useState(false);
  const [statssaMsg, setStatssaMsg] = useState(null); // { type: "ok"|"warn"|"error"|"info", text }

  const handleAutoFill = async (force = false) => {
    const parsed = parseIndexMonth(form.indexMonth);
    if (!parsed) {
      setStatssaMsg({ type: "error", text: 'Enter the Index Month first (e.g. "March 2026") so I know which StatsSA release to read.' });
      return;
    }
    setStatssaBusy(true);
    setStatssaMsg({ type: "info", text: `Reading StatsSA releases for ${parsed.monthName} ${parsed.year}…` });
    try {
      const result = await fetchStatssaIndices({ ym: parsed.ym, monthName: parsed.monthName, year: parsed.year, force });
      const { patch, filled, missing } = mapResultToIndexValues(tableDefinition.rowIndexLabels, result);
      if (filled.length) {
        setForm((prev) => ({ ...prev, indexValues: { ...prev.indexValues, ...patch } }));
      }
      const fLabels = filled.map((l) => STATSSA_SERIES_LABELS[l] || l);
      const mLabels = missing.map((l) => STATSSA_SERIES_LABELS[l] || l);
      const reason = result?.errors?.[0] ? ` ${result.errors[0]}` : "";
      if (filled.length && !missing.length) {
        setStatssaMsg({ type: "ok", text: `Auto-filled from StatsSA ${parsed.monthName} ${parsed.year}: ${fLabels.join(", ")}. Verify each value against the release before setting this entry to Final.` });
      } else if (filled.length) {
        setStatssaMsg({ type: "warn", text: `Filled ${fLabels.join(", ")}. Couldn't read ${mLabels.join(", ")} — enter those manually.${reason}` });
      } else {
        setStatssaMsg({ type: "warn", text: `No values found for ${parsed.monthName} ${parsed.year} — the StatsSA release may not be published yet, or its layout changed. Enter the values manually.${reason}` });
      }
    } catch (err) {
      setStatssaMsg({ type: "error", text: err?.message || "Auto-fill failed — enter the values manually." });
    } finally {
      setStatssaBusy(false);
    }
  };

  // Live preview of computed factor + escalation
  const previewFactor = useMemo(() => {
    const mockRow = { id: "__preview__", indexStatus: form.indexStatus, baseIndexSetId: form.baseIndexSetId, indexValues: form.indexValues, workValue: form.workValue };
    const mockTableState = { ...tableState, baseIndexSets };
    return calculateFidicFactor(tableDefinition, mockTableState, mockRow);
  }, [form, tableDefinition, tableState, baseIndexSets]);

  const previewEscalation = useMemo(() => {
    const factor = getFidicCalculationFactor(previewFactor, tableDefinition);
    return roundTo(numberOrZero(form.workValue) * factor);
  }, [previewFactor, form.workValue, tableDefinition]);

  const handleCertChange = (certNo) => {
    const cert = certificateOptions.find((c) => c.value === certNo);
    setForm((prev) => ({
      ...prev,
      certificateNo: certNo,
      indexMonth: cert?.month || prev.indexMonth,
      workValue: cert?.subtotalA !== undefined && cert?.subtotalA !== "" ? String(cert.subtotalA) : prev.workValue,
    }));
  };

  const isProvisional = form.indexStatus === "UL";

  const modalOverlay = {
    position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)", zIndex: 1000,
    display: "flex", alignItems: "center", justifyContent: "center", padding: 24,
  };
  const modalBox = {
    background: "#fff", borderRadius: 20, padding: 32, width: "100%", maxWidth: 580,
    boxShadow: "0 24px 64px rgba(15,23,42,0.22)", display: "grid", gap: 20, maxHeight: "90vh", overflowY: "auto",
  };
  const fieldLabel = { fontSize: 12, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.07em", color: "#64748b", marginBottom: 6, display: "block" };
  const fieldInput = { width: "100%", border: "1px solid #cbd5e1", borderRadius: 8, padding: "10px 12px", fontSize: 14, fontWeight: 600, boxSizing: "border-box" };
  const statusBadge = isProvisional
    ? { background: "#fff7ed", border: "1.5px solid #f97316", color: "#92400e", borderRadius: 8, padding: "4px 12px", fontWeight: 800, fontSize: 13 }
    : { background: "#f0fdf4", border: "1.5px solid #16a34a", color: "#14532d", borderRadius: 8, padding: "4px 12px", fontWeight: 800, fontSize: 13 };

  return (
    <div style={modalOverlay} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={modalBox}>
        {/* Header */}
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: "#0f172a" }}>
              {editingEntry ? "Edit Entry" : "Add Monthly Row"}
            </h3>
            <p style={{ margin: "4px 0 0", fontSize: 13, color: "#64748b" }}>{tableDefinition.title}</p>
            {editingEntry && (
              <p style={{ margin: "6px 0 0", fontSize: 12, color: "#94a3b8" }}>
                {String(editingEntry.indexStatus || "UL").toUpperCase() === "UL"
                  ? "Converting to Final will preserve this provisional entry and add a new Final row."
                  : "Editing this Final entry will update it in place."}
              </p>
            )}
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", fontSize: 22, cursor: "pointer", color: "#94a3b8", lineHeight: 1, padding: 4 }}>✕</button>
        </div>

        {/* Cert No */}
        <div>
          <label style={fieldLabel}>Certificate No</label>
          {certificateOptions.length ? (
            <select value={form.certificateNo} onChange={(e) => handleCertChange(e.target.value)} style={fieldInput}>
              <option value="">Select certificate</option>
              {certificateOptions.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          ) : (
            <input style={fieldInput} type="text" value={form.certificateNo} onChange={(e) => set("certificateNo", e.target.value)} placeholder="e.g. 001" />
          )}
        </div>

        {/* Index Month + Status side by side */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          <div>
            <label style={fieldLabel}>Index Month</label>
            <input style={fieldInput} type="text" value={form.indexMonth} onChange={(e) => set("indexMonth", e.target.value)} placeholder="e.g. May 2024" />
          </div>
          <div>
            <label style={fieldLabel}>Status</label>
            <div style={{ display: "flex", gap: 8, marginTop: 2 }}>
              {[{ val: "UL", label: "Provisional (P)" }, { val: "L", label: "Final (F)" }].map(({ val, label }) => (
                <button
                  key={val}
                  type="button"
                  onClick={() => set("indexStatus", val)}
                  style={{
                    flex: 1, padding: "10px 8px", borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: "pointer",
                    border: form.indexStatus === val ? "2px solid #2563eb" : "1px solid #cbd5e1",
                    background: form.indexStatus === val ? "#eff6ff" : "#fff",
                    color: form.indexStatus === val ? "#1d4ed8" : "#64748b",
                  }}
                >{label}</button>
              ))}
            </div>
          </div>
        </div>

        {/* Work Value */}
        <div>
          <label style={fieldLabel}>Work Value (R)</label>
          <input style={fieldInput} type="number" step="any" value={form.workValue} onChange={(e) => set("workValue", e.target.value)} placeholder="Enter work value" />
        </div>

        {/* Base Index Set selector (if multiple) */}
        {baseIndexSets.length > 1 && (
          <div>
            <label style={fieldLabel}>Base Index Set</label>
            <select value={form.baseIndexSetId} onChange={(e) => set("baseIndexSetId", e.target.value)} style={fieldInput}>
              {baseIndexSets.map((s, i) => <option key={s.id} value={s.id}>{s.label || `Base Set ${i + 1}`}</option>)}
            </select>
          </div>
        )}

        {/* Index values */}
        <div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 6 }}>
            <label style={{ ...fieldLabel, marginBottom: 0 }}>Index Values</label>
            {statssaLabels.length > 0 && (
              <button
                type="button"
                onClick={() => handleAutoFill(false)}
                disabled={statssaBusy}
                style={{
                  display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 12px", borderRadius: 8,
                  border: "1px solid #2563eb", background: statssaBusy ? "#eff6ff" : "#fff", color: "#1d4ed8",
                  fontWeight: 700, fontSize: 12, cursor: statssaBusy ? "default" : "pointer", whiteSpace: "nowrap",
                }}
              >{statssaBusy ? "Reading StatsSA…" : "⤓ Auto-fill from StatsSA"}</button>
            )}
          </div>
          {statssaLabels.length > 0 && (
            <p style={{ margin: "0 0 8px", fontSize: 11, color: "#94a3b8", lineHeight: 1.45 }}>
              Reads {statssaLabels.length} value{statssaLabels.length > 1 ? "s" : ""} from the StatsSA release for the Index Month above.
              Best-effort — StatsSA publishes late and changes layouts, so always check each value against the release and enter anything it misses by hand.
            </p>
          )}
          {statssaMsg && (
            <div style={{
              margin: "0 0 10px", padding: "8px 12px", borderRadius: 8, fontSize: 12, fontWeight: 600, lineHeight: 1.4,
              ...(statssaMsg.type === "ok"    ? { background: "#f0fdf4", border: "1px solid #86efac", color: "#14532d" } : {}),
              ...(statssaMsg.type === "warn"  ? { background: "#fff7ed", border: "1px solid #fdba74", color: "#92400e" } : {}),
              ...(statssaMsg.type === "error" ? { background: "#fef2f2", border: "1px solid #fca5a5", color: "#991b1b" } : {}),
              ...(statssaMsg.type === "info"  ? { background: "#eff6ff", border: "1px solid #bfdbfe", color: "#1e40af" } : {}),
            }}>
              {statssaMsg.text}
              {(statssaMsg.type === "warn" || statssaMsg.type === "error") && !statssaBusy && (
                <button
                  type="button"
                  onClick={() => handleAutoFill(true)}
                  style={{ marginLeft: 8, background: "none", border: "none", padding: 0, color: "inherit", fontWeight: 800, textDecoration: "underline", cursor: "pointer", fontSize: 12 }}
                >Try again</button>
              )}
            </div>
          )}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))", gap: 10 }}>
            {tableDefinition.rowIndexLabels.map((lbl) => (
              <div key={lbl}>
                <label style={{ ...fieldLabel, marginBottom: 4 }}>{lbl}</label>
                <input
                  style={{ ...fieldInput, ...(isProvisional ? { borderColor: "#f97316", background: "#fff7ed", color: "#92400e" } : {}) }}
                  type="number"
                  step="any"
                  value={form.indexValues[lbl] ?? ""}
                  onChange={(e) => setIndex(lbl, e.target.value)}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Live preview */}
        <div style={{ background: "#f8fbff", border: "1px solid #dbe7f5", borderRadius: 12, padding: 16, display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
          <div>
            <p style={{ ...fieldLabel, marginBottom: 2 }}>Factor (f)</p>
            <p style={{ margin: 0, fontWeight: 800, fontSize: 16, color: "#0f172a" }}>{formatFactor(getRoundedFidicFactor(previewFactor, tableDefinition), tableDefinition.factorDisplayDecimals || 4)}</p>
          </div>
          <div>
            <p style={{ ...fieldLabel, marginBottom: 2 }}>Escalation</p>
            <p style={{ margin: 0, fontWeight: 800, fontSize: 16, color: "#0f172a" }}>{formatAmount(previewEscalation)}</p>
          </div>
          <div>
            <p style={{ ...fieldLabel, marginBottom: 2 }}>Status</p>
            <span style={statusBadge}>{isProvisional ? "Provisional" : "Final"}</span>
          </div>
        </div>

        {/* Actions */}
        <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
          <button onClick={onClose} style={SECTION_STYLES.secondaryButton}>Cancel</button>
          <button
            onClick={() => onSave(form)}
            style={SECTION_STYLES.primaryButton}
          >
            {editingEntry
              ? (String(editingEntry.indexStatus || "UL").toUpperCase() === "UL"
                  ? "Save as New Entry"
                  : "Update Entry")
              : "Save to Register"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main FIDIC CPA table component ──────────────────────────────────────────
function FidicCpaTable({ tableDefinition, tableState, updateTable, addRow, removeRow, storedCertificates = [], storedCertificateByNo = {}, soqItems = [], filterUpToCert = "" }) {
  const [modalOpen, setModalOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null); // null = new, row object = editing

  const baseIndexSets = normaliseBaseIndexSets(tableDefinition, tableState);
  const factorTotal = tableDefinition.factorKeys.reduce(
    (sum, key) => sum + numberOrZero(tableState.factors?.[key]),
    0
  );

  // Live workValue per row from SOQ
  const liveWorkValues = useMemo(() => {
    const result = {};
    (tableState.rows || []).forEach((row) => {
      const certNo = String(row.certificateNo || "").trim().toLowerCase();
      if (!certNo) return;
      const linkedCert = storedCertificateByNo[certNo];
      if (!linkedCert) return;
      const computed = computeCategoryWorkValue(tableDefinition.key, linkedCert, soqItems);
      if (computed !== "") result[row.id] = computed;
    });
    return result;
  }, [tableState.rows, storedCertificateByNo, soqItems, tableDefinition.key]);

  const effectiveTableState = useMemo(() => {
    const hasAnyLive = Object.keys(liveWorkValues).length > 0;
    if (!hasAnyLive) return tableState;
    return {
      ...tableState,
      rows: (tableState.rows || []).map((row) =>
        liveWorkValues[row.id] !== undefined ? { ...row, workValue: liveWorkValues[row.id] } : row
      ),
    };
  }, [tableState, liveWorkValues]);

  const computedTable = calculateFidicTable(tableDefinition, effectiveTableState);

  // ─── Ruling CPA logic ───────────────────────────────────────────────────────
  // For each cert, if a Final entry exists use its carriedCpaAmount, otherwise use Provisional.
  // Build a map: certNo → ruling computed row (prefer Final over Provisional).
  const rulingRowByCert = useMemo(() => {
    const map = {};
    (computedTable.rows || []).forEach((row) => {
      const cert = String(row.certificateNo || "").trim();
      if (!cert) return;
      const existing = map[cert];
      const isFinal = String(row.indexStatus || "UL").toUpperCase() === "L";
      // Final beats Provisional; if both are Final, last one wins (append order)
      if (!existing || isFinal) map[cert] = row;
    });
    return map;
  }, [computedTable.rows]);

  // Running cumulative of ruling CPA by cert order
  const rulingCumulativeByCert = useMemo(() => {
    const certOrder = Object.keys(rulingRowByCert).sort((a, b) =>
      String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: "base" })
    );
    let running = 0;
    const result = {};
    certOrder.forEach((cert) => {
      const row = rulingRowByCert[cert];
      running = roundTo(running + numberOrZero(row.computed?.carriedCpaAmount));
      result[cert] = running;
    });
    return result;
  }, [rulingRowByCert]);

  // Filter + sort rows ascending by cert no
  const filteredFidicRows = useMemo(() => {
    let rows = computedTable.rows;
    if (filterUpToCert) {
      const targetNorm = String(filterUpToCert).trim().toLowerCase();
      const idx = rows.findIndex((row) => String(row.certificateNo || "").trim().toLowerCase() === targetNorm);
      if (idx !== -1) {
        rows = rows.slice(0, idx + 1);
      } else {
        const targetNum = parseFloat(targetNorm.replace(/[^0-9.]/g, ""));
        if (!isNaN(targetNum)) {
          const numericRows = rows.filter((row) => {
            const rowNum = parseFloat(String(row.certificateNo || "").trim().replace(/[^0-9.]/g, ""));
            return !isNaN(rowNum) && rowNum <= targetNum;
          });
          if (numericRows.length) rows = numericRows;
        }
      }
    }
    return [...rows].sort((a, b) =>
      String(a.certificateNo || "").localeCompare(String(b.certificateNo || ""), undefined, { numeric: true, sensitivity: "base" })
    );
  }, [computedTable.rows, filterUpToCert]);

  // ─── Factor / base index set editors ──────────────────────────────────────
  const updateFactor = (factorKey, value) => updateTable(tableDefinition.key, { factors: { ...(tableState.factors || {}), [factorKey]: value } });

  const updateBaseIndexSetLabel = (setId, value) =>
    updateTable(tableDefinition.key, { baseIndexSets: baseIndexSets.map((s) => s.id === setId ? { ...s, label: value } : s) });

  const updateBaseIndexSetRevisedMonth = (setId, value) =>
    updateTable(tableDefinition.key, { baseIndexSets: baseIndexSets.map((s) => s.id === setId ? { ...s, revisedMonth: value } : s) });

  const updateBaseIndexSetValue = (setId, indexKey, value) =>
    updateTable(tableDefinition.key, {
      baseIndexSets: baseIndexSets.map((s) =>
        s.id === setId ? { ...s, baseIndices: { ...(s.baseIndices || {}), [indexKey]: value } } : s
      ),
    });

  const addBaseIndexSet = () => {
    const nextIndex = baseIndexSets.length;
    updateTable(tableDefinition.key, {
      baseIndexSets: [...baseIndexSets, createDefaultBaseIndexSet(tableDefinition, nextIndex, baseIndexSets[baseIndexSets.length - 1]?.baseIndices || tableState.baseIndices || {})],
    });
  };

  const removeBaseIndexSet = (setId) => {
    if (baseIndexSets.length <= 1) return;
    const nextSets = baseIndexSets.filter((s) => s.id !== setId);
    const fallbackSetId = nextSets[0]?.id || "base-set-1";
    updateTable(tableDefinition.key, {
      baseIndexSets: nextSets,
      rows: (tableState.rows || []).map((row) => ({ ...row, baseIndexSetId: row.baseIndexSetId === setId ? fallbackSetId : row.baseIndexSetId })),
    });
  };

  // ─── Save from modal ───────────────────────────────────────────────────────
  // Always appends a new entry (register pattern). Editing an existing row pre-fills the
  // modal but on Save creates a brand-new row — original is preserved.
  const handleModalSave = (form) => {
    const existingRow = editingEntry;
    const wasProvisional = existingRow && String(existingRow?.indexStatus || "UL").toUpperCase() === "UL";
    const isNowFinal = String(form.indexStatus || "UL").toUpperCase() === "L";

    if (existingRow && wasProvisional && isNowFinal) {
      // Converting Provisional → Final: preserve original row, append new Final entry
      const newRow = {
        id: `FIDIC-CPA-ROW-${Date.now()}`,
        certificateNo: form.certificateNo,
        certificateDate: "",
        indexMonth: form.indexMonth,
        indexStatus: form.indexStatus,
        baseIndexSetId: form.baseIndexSetId || baseIndexSets[0]?.id || "base-set-1",
        workValue: form.workValue,
        lockedProvisionalCpaAmount: "",
        notes: "",
        indexValues: { ...form.indexValues },
        _copiedFromPrevious: false,
      };
      updateTable(tableDefinition.key, {
        rows: [...(tableState.rows || []), newRow],
      });
    } else if (existingRow) {
      // Editing existing row (Final updating in-place, or Provisional staying Provisional)
      updateTable(tableDefinition.key, {
        rows: (tableState.rows || []).map((r) =>
          r.id === existingRow.id
            ? {
                ...r,
                certificateNo: form.certificateNo,
                indexMonth: form.indexMonth,
                indexStatus: form.indexStatus,
                baseIndexSetId: form.baseIndexSetId || baseIndexSets[0]?.id || "base-set-1",
                workValue: form.workValue,
                indexValues: { ...form.indexValues },
              }
            : r
        ),
      });
    } else {
      // New entry
      const newRow = {
        id: `FIDIC-CPA-ROW-${Date.now()}`,
        certificateNo: form.certificateNo,
        certificateDate: "",
        indexMonth: form.indexMonth,
        indexStatus: form.indexStatus,
        baseIndexSetId: form.baseIndexSetId || baseIndexSets[0]?.id || "base-set-1",
        workValue: form.workValue,
        lockedProvisionalCpaAmount: "",
        notes: "",
        indexValues: { ...form.indexValues },
        _copiedFromPrevious: false,
      };
      updateTable(tableDefinition.key, {
        rows: [...(tableState.rows || []), newRow],
      });
    }
    setModalOpen(false);
    setEditingEntry(null);
  };

  const openAddModal = () => {
    setEditingEntry(null);
    setModalOpen(true);
  };

  const openEditModal = (row) => {
    setEditingEntry(row);
    setModalOpen(true);
  };

  // ─── Totals for the register footer ───────────────────────────────────────
  // Use ruling CPA totals: per cert, take the ruling (Final > Provisional) row.
  const rulingRows = Object.values(rulingRowByCert);
  const totalRulingCpa = roundTo(rulingRows.reduce((s, r) => s + numberOrZero(r.computed?.carriedCpaAmount), 0));
  const totalProvisionalCpa = roundTo(filteredFidicRows.reduce((s, r) => s + numberOrZero(r.computed?.provisionalCpaAmount), 0));
  const totalFinalCpa = roundTo(filteredFidicRows.reduce((s, r) => s + numberOrZero(r.computed?.finalCpaAmount), 0));

  return (
    <div style={{ ...SECTION_STYLES.sectionCard, borderColor: tableState.enabled === false ? "#e2e8f0" : "#dbe7f5" }}>
      {/* Modal */}
      {modalOpen && (
        <CpaEntryModal
          tableDefinition={tableDefinition}
          tableState={tableState}
          baseIndexSets={baseIndexSets}
          storedCertificates={storedCertificates}
          editingEntry={editingEntry}
          onSave={handleModalSave}
          onClose={() => { setModalOpen(false); setEditingEntry(null); }}
        />
      )}

      {/* Header */}
      <div style={SECTION_STYLES.sectionHeader}>
        <div>
          <h3 style={SECTION_STYLES.sectionTitle}>{tableDefinition.title}</h3>
          <p style={SECTION_STYLES.sectionSubtitle}>{tableDefinition.formula}</p>
          {tableDefinition.note && <p style={SECTION_STYLES.sectionSubtitle}>{tableDefinition.note}</p>}
        </div>
        <div style={SECTION_STYLES.buttonRow}>
          <label style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 800 }}>
            <input type="checkbox" checked={tableState.enabled !== false} onChange={(e) => updateTable(tableDefinition.key, { enabled: e.target.checked })} />
            Use for this project
          </label>
          <button onClick={openAddModal} style={SECTION_STYLES.primaryButton}>Add Monthly Row</button>
        </div>
      </div>

      {/* KPI strip */}
      <div style={SECTION_STYLES.kpiGrid}>
        <div style={SECTION_STYLES.kpiCard}>
          <p style={SECTION_STYLES.kpiLabel}>Base Month</p>
          <input type="text" value={tableState.baseMonth || ""} onChange={(e) => updateTable(tableDefinition.key, { baseMonth: e.target.value })} />
        </div>
        <div style={SECTION_STYLES.kpiCard}>
          <p style={SECTION_STYLES.kpiLabel}>Base Year</p>
          <input type="text" value={tableState.baseYear || ""} onChange={(e) => updateTable(tableDefinition.key, { baseYear: e.target.value })} />
        </div>
        {tableDefinition.nonAdjustableKey && (
          <div style={SECTION_STYLES.kpiCard}>
            <p style={SECTION_STYLES.kpiLabel}>x — Non-adjustable Portion</p>
            <input type="number" step="any" value={tableState.nonAdjustable ?? ""} onChange={(e) => updateTable(tableDefinition.key, { nonAdjustable: e.target.value })} />
          </div>
        )}
        <div style={SECTION_STYLES.kpiCard}>
          <p style={SECTION_STYLES.kpiLabel}>Table Escalation (Ruling)</p>
          <p style={SECTION_STYLES.kpiValue}>{formatAmount(tableState.enabled === false ? 0 : totalRulingCpa)}</p>
        </div>
      </div>

      {/* Contract Factors */}
      <div style={SECTION_STYLES.tableWrap}>
        <table>
          <thead>
            <tr>
              <th>Factor</th>
              {tableDefinition.factorKeys.map((k) => <th key={k}>{k}</th>)}
              <th>Check</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={SECTION_STYLES.rowLabel}>Contract Factors</td>
              {tableDefinition.factorKeys.map((k) => (
                <td key={k}>
                  <input type="number" step="any" value={tableState.factors?.[k] ?? ""} onChange={(e) => updateFactor(k, e.target.value)} />
                </td>
              ))}
              <td style={SECTION_STYLES.readonlyCell}>{formatIndex(factorTotal)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Base Index Sets */}
      <div style={SECTION_STYLES.tableWrap}>
        <table>
          <thead>
            <tr>
              <th>Base Set</th>
              <th>Month Indice Revised</th>
              {tableDefinition.rowIndexLabels.map((lbl) => <th key={lbl}>{tableDefinition.baseIndexLabels?.[lbl] || lbl}</th>)}
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {baseIndexSets.map((set, si) => (
              <tr key={set.id}>
                <td style={SECTION_STYLES.rowLabel}>
                  <input type="text" value={set.label || `Base Set ${si + 1}`} onChange={(e) => updateBaseIndexSetLabel(set.id, e.target.value)} />
                </td>
                <td>
                  <input type="text" placeholder="e.g. May 2023" value={set.revisedMonth || ""} onChange={(e) => updateBaseIndexSetRevisedMonth(set.id, e.target.value)} />
                </td>
                {tableDefinition.rowIndexLabels.map((lbl) => (
                  <td key={`${set.id}-${lbl}`}>
                    <input type="number" step="any"
                      value={set.baseIndices?.[lbl] ?? tableState.baseIndices?.[lbl] ?? getRebasedBaseIndex(tableDefinition, lbl) ?? ""}
                      onChange={(e) => updateBaseIndexSetValue(set.id, lbl, e.target.value)}
                    />
                  </td>
                ))}
                <td>
                  <button type="button" onClick={() => removeBaseIndexSet(set.id)} style={SECTION_STYLES.dangerButton} disabled={baseIndexSets.length <= 1}>Delete</button>
                </td>
              </tr>
            ))}
            <tr>
              <td colSpan={3 + tableDefinition.rowIndexLabels.length}>
                <button type="button" onClick={addBaseIndexSet} style={SECTION_STYLES.secondaryButton}>Add Base Index Set</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* ─── CPA Register ─────────────────────────────────────────────────── */}
      <div style={SECTION_STYLES.tableWrap}>
        <table style={{ borderCollapse: "collapse", width: "100%" }}>
          <thead>
            <tr>
              {["Entry","Cert No","Index Month",...tableDefinition.rowIndexLabels,"Factor (f)","Work Value","Status","Provisional CPA","Final CPA","Ruling CPA","Cumulative Ruling","Actions"].map((h, i, arr) => (
                <th key={h + i} style={{ borderRight: i < arr.length - 1 ? "1px solid #dbe7f5" : "none", borderBottom: "1px solid #dbe7f5", whiteSpace: "nowrap" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredFidicRows.length === 0 ? (
              <tr>
                <td colSpan={12 + tableDefinition.rowIndexLabels.length} style={{ textAlign: "center", color: "#64748b", padding: 20 }}>
                  No entries yet — click <strong>Add Monthly Row</strong> to capture your first CPA entry.
                </td>
              </tr>
            ) : filteredFidicRows.map((row, idx) => {
              const isFinal = String(row.indexStatus || "UL").toUpperCase() === "L";
              const certKey = String(row.certificateNo || "").trim();
              const isRuling = rulingRowByCert[certKey]?.id === row.id;
              const rulingCpa = isRuling ? numberOrZero(row.computed?.carriedCpaAmount) : null;
              const cumRuling = isRuling ? rulingCumulativeByCert[certKey] : null;
              const rowBg = isFinal ? "#f0fdf4" : "#fffbf5";
              const cb = { borderRight: "1px solid #dbe7f5", borderBottom: "1px solid #dbe7f5" };

              return (
                <tr key={row.id} style={{ background: rowBg }}>
                  <td style={{ ...cb, fontWeight: 700, color: "#64748b", fontSize: 12 }}>#{idx + 1}</td>
                  <td style={{ ...cb, fontWeight: 700 }}>{row.certificateNo || "—"}</td>
                  <td style={cb}>{row.indexMonth || "—"}</td>
                  {tableDefinition.rowIndexLabels.map((lbl) => (
                    <td key={`${row.id}-${lbl}`} style={{ ...cb, fontSize: 13 }}>
                      {row.indexValues?.[lbl] !== undefined && row.indexValues[lbl] !== "" ? row.indexValues[lbl] : "—"}
                    </td>
                  ))}
                  <td style={{ ...cb, fontSize: 13 }}>{formatFactor(row.computed?.factor, tableDefinition.factorDisplayDecimals || 4)}</td>
                  <td style={{ ...cb, fontSize: 13 }}>{formatAmount(row.computed?.workValue)}</td>
                  <td style={cb}>
                    <span style={{
                      padding: "3px 10px", borderRadius: 20, fontSize: 12, fontWeight: 800,
                      background: isFinal ? "#dcfce7" : "#fff7ed",
                      color: isFinal ? "#15803d" : "#c2410c",
                      border: `1px solid ${isFinal ? "#86efac" : "#fdba74"}`,
                    }}>
                      {isFinal ? "Final" : "Provisional"}
                    </span>
                  </td>
                  <td style={{ ...cb, color: "#f97316", fontWeight: 700 }}>
                    {formatAmount(row.computed?.provisionalCpaAmount)}
                  </td>
                  <td style={{ ...cb, color: "#16a34a", fontWeight: 700 }}>
                    {isFinal ? formatAmount(row.computed?.finalCpaAmount) : "—"}
                  </td>
                  <td style={{ ...cb, color: isRuling ? "#0f172a" : "#94a3b8", fontWeight: isRuling ? 800 : 500 }}>
                    {isRuling ? formatAmount(rulingCpa) : <span style={{ fontSize: 11 }}>superseded</span>}
                  </td>
                  <td style={{ ...cb, color: "#0f172a", fontWeight: isRuling ? 800 : 400 }}>
                    {isRuling ? formatAmount(cumRuling) : "—"}
                  </td>
                  <td style={{ borderBottom: "1px solid #dbe7f5" }}>
                    <div style={{ display: "flex", gap: 6 }}>
                      <button onClick={() => openEditModal(row)} style={SECTION_STYLES.compactButton}>Edit</button>
                      <button onClick={() => removeRow(tableDefinition.key, row.id)} style={SECTION_STYLES.dangerButton}>Delete</button>
                    </div>
                  </td>
                </tr>
              );
            })}

            {/* Footer totals */}
            {filteredFidicRows.length > 0 && (
              <tr style={{ background: "#f8fbff", fontWeight: 800 }}>
                <td colSpan={4 + tableDefinition.rowIndexLabels.length} style={{ borderRight: "1px solid #dbe7f5", borderTop: "2px solid #dbe7f5", textAlign: "right", color: "#0f172a", paddingRight: 12 }}>
                  Totals
                </td>
                <td style={{ borderRight: "1px solid #dbe7f5", borderTop: "2px solid #dbe7f5" }}></td>
                <td style={{ borderRight: "1px solid #dbe7f5", borderTop: "2px solid #dbe7f5" }}></td>
                <td style={{ borderRight: "1px solid #dbe7f5", borderTop: "2px solid #dbe7f5", color: "#f97316" }}>{formatAmount(totalProvisionalCpa)}</td>
                <td style={{ borderRight: "1px solid #dbe7f5", borderTop: "2px solid #dbe7f5", color: "#16a34a" }}>{formatAmount(totalFinalCpa)}</td>
                <td style={{ borderRight: "1px solid #dbe7f5", borderTop: "2px solid #dbe7f5", color: "#0f172a" }}>{formatAmount(totalRulingCpa)}</td>
                <td style={{ borderRight: "1px solid #dbe7f5", borderTop: "2px solid #dbe7f5", color: "#0f172a" }}>{formatAmount(totalRulingCpa)}</td>
                <td style={{ borderTop: "2px solid #dbe7f5" }}></td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Collapsible audit section ───────────────────────────────────────────────
function CollapsibleAuditSection({ title, subtitle, children, defaultOpen = false }) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div style={SECTION_STYLES.sectionCard}>
      <div style={SECTION_STYLES.sectionHeader}>
        <div style={{ flex: 1 }}>
          <h3 style={SECTION_STYLES.sectionTitle}>{title}</h3>
          {subtitle && <p style={SECTION_STYLES.sectionSubtitle}>{subtitle}</p>}
        </div>
        <button
          onClick={() => setIsOpen((prev) => !prev)}
          style={{
            ...SECTION_STYLES.secondaryButton,
            display: "flex",
            alignItems: "center",
            gap: 8,
            fontSize: 13,
            minWidth: 90,
            justifyContent: "center",
          }}
        >
          <span
            style={{
              display: "inline-block",
              transition: "transform 0.2s ease",
              transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
              fontSize: 9,
              lineHeight: 1,
            }}
          >
            ▼
          </span>
          {isOpen ? "Hide" : "Show"}
        </button>
      </div>
      {isOpen && children}
    </div>
  );
}

function Toast({ message, visible }) {
  return (
    <div
      style={{
        position: "fixed",
        bottom: 32,
        left: "50%",
        transform: `translateX(-50%) translateY(${visible ? 0 : 20}px)`,
        opacity: visible ? 1 : 0,
        transition: "opacity 0.25s ease, transform 0.25s ease",
        background: "#0f172a",
        color: "#fff",
        padding: "12px 20px",
        borderRadius: 12,
        fontWeight: 700,
        fontSize: 14,
        boxShadow: "0 8px 24px rgba(15,23,42,0.25)",
        pointerEvents: "none",
        zIndex: 9999,
        whiteSpace: "nowrap",
      }}
    >
      {message}
    </div>
  );
}

// ─── Compute the total "this month amount" for a given FIDIC table key ────────
// Looks up all SOQ items whose linkedCpaTableId matches the table key,
// then sums the amountThisMonth for those items in the certificate's claimedItems.
function computeCategoryWorkValue(tableKey, certificate, soqItems = []) {
  if (!certificate || !tableKey) return "";

  // Build a set of itemNos that belong to this CPA table category
  const categoryItemNos = new Set(
    (soqItems || [])
      .filter((item) => String(item.linkedCpaTableId || "").trim() === String(tableKey).trim())
      .map((item) => String(item.itemNo || "").trim().toUpperCase())
  );

  if (categoryItemNos.size === 0) return "";

  // Sum amountThisMonth for matching claimed items on this certificate
  const total = (certificate.claimedItems || []).reduce((sum, claimed) => {
    const itemNo = String(claimed.itemNo || "").trim().toUpperCase();
    if (categoryItemNos.has(itemNo)) {
      return sum + numberOrZero(claimed.amountThisMonth);
    }
    return sum;
  }, 0);

  return total > 0 ? String(roundTo(total)) : "";
}

export default function CPASection({
  currentProjectId,
  contractType = "GCC",
  cpaColumns = [],
  setCpaColumns,
  fidicCpaTables,
  setFidicCpaTables,
  availableCertificateMonths = [],
  storedCertificates = [],
  soqItems = [],
  handleViewItem,
}) {
  const [localFidicCpaTables, setLocalFidicCpaTables] = useState(() => createDefaultFidicCpaState());
  const [localGccBaseIndexSets, setLocalGccBaseIndexSets] = useState(() => normaliseGccBaseIndexSets([]));
  const [toast, setToast] = useState({ visible: false, message: "" });
  const [snapshotCertFilter, setSnapshotCertFilter] = useState("");
  // Per-column StatsSA auto-fill state: { [columnId]: { busy, msg: {type,text} } }
  const [gccStatssa, setGccStatssa] = useState({});
  // Per-base-set StatsSA auto-fill state: { [setId]: { busy, msg: {type,text} } }
  const [baseStatssa, setBaseStatssa] = useState({});

  // Which P0151.1 Table 6 row this project escalates materials on. Persisted on
  // the project doc so certificates/CPA agree everywhere.
  const [materialSeriesKey, setMaterialSeriesKey] = useState(MATERIAL_SERIES_DEFAULT_KEY);
  useEffect(() => {
    let alive = true;
    if (!currentProjectId) return undefined;
    getDoc(doc(db, "projects", currentProjectId))
      .then((snap) => {
        if (alive && snap.exists() && snap.data().statssaMaterialSeries) {
          setMaterialSeriesKey(snap.data().statssaMaterialSeries);
        }
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [currentProjectId]);
  const persistMaterialSeries = async (key) => {
    setMaterialSeriesKey(key);
    if (!currentProjectId) return;
    try {
      await updateDoc(doc(db, "projects", currentProjectId), { statssaMaterialSeries: key });
    } catch (e) { /* non-fatal: selection still applies for this session */ }
  };
  const effectiveContractType = String(contractType || "GCC").toUpperCase() === "FIDIC" ? "FIDIC" : "GCC";
  const fidicTablesSource = fidicCpaTables || localFidicCpaTables;
  const persistFidicTables = setFidicCpaTables || setLocalFidicCpaTables;

  const storedCertificateByNo = useMemo(
    () => buildCertificateLookup(storedCertificates),
    [storedCertificates]
  );

  const gccBaseIndexSets = useMemo(() => {
    return (cpaColumns || []).length
      ? normaliseGccBaseIndexSets(cpaColumns)
      : localGccBaseIndexSets;
  }, [cpaColumns, localGccBaseIndexSets]);

  const gccColumnsForCalculation = useMemo(
    () =>
      (cpaColumns || []).map((column) => ({
        ...column,
        baseIndexSets:
          Array.isArray(column.baseIndexSets) && column.baseIndexSets.length
            ? column.baseIndexSets
            : gccBaseIndexSets,
      })),
    [cpaColumns, gccBaseIndexSets]
  );

  const computedColumns = useMemo(
    () =>
      buildComputedColumns(gccColumnsForCalculation).map((column) => {
        const linkedCertificate = storedCertificateByNo[normaliseCertificateNo(column.certificateNo)];
        const issuedLockedCpaThisCertificate = numberOrZero(
          linkedCertificate?.lockedCpaThisCertificate ?? linkedCertificate?.cpaThisCertificate
        );
        const issuedLockedCpaCumulative = numberOrZero(
          linkedCertificate?.lockedCpaCumulative ?? linkedCertificate?.cpaCumulative
        );
        const adjustmentFromIssuedCpa = roundTo(
          numberOrZero(column.computed?.cpaTotal) - issuedLockedCpaThisCertificate
        );
        const cumulativeAdjustmentFromIssued = roundTo(
          numberOrZero(column.computed?.totalCumulativeCpa) - issuedLockedCpaCumulative
        );

        return {
          ...column,
          linkedCertificate,
          computed: {
            ...(column.computed || {}),
            issuedLockedCpaThisCertificate,
            issuedLockedCpaCumulative,
            adjustmentFromIssuedCpa,
            cumulativeAdjustmentFromIssued,
          },
        };
      }),
    [gccColumnsForCalculation, storedCertificateByNo]
  );
  const normalisedFidicTables = useMemo(() => normaliseFidicCpaState(fidicTablesSource), [fidicTablesSource]);
  const fidicTotals = useMemo(() => calculateFidicCpaTotals(normalisedFidicTables), [normalisedFidicTables]);
  const fidicCertificateAuditRows = useMemo(() => {
    const grouped = {};

    FIDIC_CPA_TABLES.forEach((tableDefinition) => {
      const computedTable = fidicTotals.computedTables?.[tableDefinition.key];
      if (!computedTable || computedTable.enabled === false) return;

      (computedTable.rows || []).forEach((row) => {
        const certificateNo = String(row.certificateNo || "").trim();
        if (!certificateNo) return;

        if (!grouped[certificateNo]) {
          grouped[certificateNo] = {
            certificateNo,
            certificateDate: row.certificateDate || "",
            indexMonth: row.indexMonth || "",
            revisedCpaThisCertificate: 0,
            tableBreakdown: [],
          };
        }

        const escalation = numberOrZero(row.computed?.carriedCpaAmount);
        grouped[certificateNo].revisedCpaThisCertificate = roundTo(
          grouped[certificateNo].revisedCpaThisCertificate + escalation
        );
        grouped[certificateNo].tableBreakdown.push({
          table: tableDefinition.title,
          escalation,
          indexStatus: row.indexStatus || "UL",
        });
      });
    });

    let runningRevisedCumulative = 0;

    return Object.values(grouped)
      .sort((a, b) =>
        String(a.certificateNo).localeCompare(String(b.certificateNo), undefined, {
          numeric: true,
          sensitivity: "base",
        })
      )
      .map((row) => {
        runningRevisedCumulative = roundTo(runningRevisedCumulative + numberOrZero(row.revisedCpaThisCertificate));
        const linkedCertificate = storedCertificateByNo[normaliseCertificateNo(row.certificateNo)];
        const issuedLockedCpaThisCertificate = numberOrZero(
          linkedCertificate?.lockedCpaThisCertificate ?? linkedCertificate?.cpaThisCertificate
        );
        const issuedLockedCpaCumulative = numberOrZero(
          linkedCertificate?.lockedCpaCumulative ?? linkedCertificate?.cpaCumulative
        );

        return {
          ...row,
          linkedCertificate,
          revisedCpaCumulative: runningRevisedCumulative,
          issuedLockedCpaThisCertificate,
          issuedLockedCpaCumulative,
          adjustmentFromIssuedCpa: roundTo(numberOrZero(row.revisedCpaThisCertificate) - issuedLockedCpaThisCertificate),
          cumulativeAdjustmentFromIssued: roundTo(runningRevisedCumulative - issuedLockedCpaCumulative),
        };
      });
  }, [fidicTotals, storedCertificateByNo]);
  const certificateOptions = useMemo(
    () =>
      (storedCertificates || [])
        .map((certificate) => {
          const certificateNo =
            certificate.certificateNo ||
            certificate.certificateNumber ||
            certificate.paymentCertificateNo ||
            certificate.pcNo ||
            certificate.id ||
            "";
          const certificateDate = certificate.certificateDate || certificate.date || "";
          const certificateMonth = certificate.certificateMonth || certificate.month || "";
          const labelParts = [certificateNo, certificateMonth || certificateDate].filter(Boolean);

          return {
            value: String(certificateNo || ""),
            label: labelParts.length ? labelParts.join(" — ") : String(certificateNo || ""),
            certificateDate,
            certificateMonth,
            // Cumulative escalatable base to date (Phase 3) — falls back to Subtotal A.
            cpaScheduledItemsCumulative:
              certificate.cpaScheduledItemsCumulative ?? certificate.subtotalA ?? certificate.effectiveSubtotalA ?? "",
          };
        })
        .filter((certificate) => certificate.value),
    [storedCertificates]
  );

  const totalColumns = computedColumns.length;

  const filteredGccColumns = useMemo(() => {
    if (!snapshotCertFilter) return computedColumns;
    const targetNorm = String(snapshotCertFilter).trim().toLowerCase();
    const idx = computedColumns.findIndex(
      (col) => String(col.certificateNo || "").trim().toLowerCase() === targetNorm
    );
    return idx === -1 ? computedColumns : computedColumns.slice(0, idx + 1);
  }, [computedColumns, snapshotCertFilter]);
  const latestGccCumulativeCpa = totalColumns
    ? numberOrZero(computedColumns[totalColumns - 1]?.computed?.totalCumulativeCpa)
    : 0;
  const latestCumulativeCpa = effectiveContractType === "FIDIC" ? fidicTotals.combinedTotalCpa : latestGccCumulativeCpa;

  // Filtered KPI: cumulative CPA at the snapshot cert selection
  const filteredGccCumulativeCpa = filteredGccColumns.length
    ? numberOrZero(filteredGccColumns[filteredGccColumns.length - 1]?.computed?.totalCumulativeCpa)
    : 0;

  const filteredFidicCombinedCpa = useMemo(() => {
    if (!snapshotCertFilter) return fidicTotals.combinedTotalCpa;
    const targetNorm = String(snapshotCertFilter).trim().toLowerCase();
    const targetNum = parseFloat(targetNorm.replace(/[^0-9.]/g, ""));
    let total = 0;
    FIDIC_CPA_TABLES.forEach((tableDef) => {
      const computedTable = fidicTotals.computedTables?.[tableDef.key];
      if (!computedTable || computedTable.enabled === false) return;
      const rows = computedTable.rows || [];
      // Try exact match first, then numeric fallback
      const idx = rows.findIndex((r) => String(r.certificateNo || "").trim().toLowerCase() === targetNorm);
      const sliced = idx !== -1
        ? rows.slice(0, idx + 1)
        : !isNaN(targetNum)
          ? rows.filter((r) => {
              const rNum = parseFloat(String(r.certificateNo || "").trim().replace(/[^0-9.]/g, ""));
              return !isNaN(rNum) && rNum <= targetNum;
            })
          : rows;
      sliced.forEach((r) => { total += numberOrZero(r.computed?.carriedCpaAmount); });
    });
    return roundTo(total);
  }, [snapshotCertFilter, fidicTotals]);

  const filteredFidicTableTotals = useMemo(() => {
    if (!snapshotCertFilter) return null;
    const targetNorm = String(snapshotCertFilter).trim().toLowerCase();
    const targetNum = parseFloat(targetNorm.replace(/[^0-9.]/g, ""));
    const result = {};
    FIDIC_CPA_TABLES.forEach((tableDef) => {
      const computedTable = fidicTotals.computedTables?.[tableDef.key];
      if (!computedTable || computedTable.enabled === false) { result[tableDef.key] = 0; return; }
      const rows = computedTable.rows || [];
      const idx = rows.findIndex((r) => String(r.certificateNo || "").trim().toLowerCase() === targetNorm);
      const sliced = idx !== -1
        ? rows.slice(0, idx + 1)
        : !isNaN(targetNum)
          ? rows.filter((r) => {
              const rNum = parseFloat(String(r.certificateNo || "").trim().replace(/[^0-9.]/g, ""));
              return !isNaN(rNum) && rNum <= targetNum;
            })
          : rows;
      result[tableDef.key] = roundTo(
  sliced.reduce((s, r) => s + numberOrZero(r.computed?.carriedCpaAmount), 0)
);
    });
    return result;
  }, [snapshotCertFilter, fidicTotals]);

  const displayCumulativeCpa = snapshotCertFilter
    ? (effectiveContractType === "FIDIC" ? filteredFidicCombinedCpa : filteredGccCumulativeCpa)
    : latestCumulativeCpa;

  const updateColumn = (columnId, field, value) => {
    if (!setCpaColumns) return;
    setCpaColumns((prev) =>
      (prev || []).map((column) =>
        column.id === columnId
          ? {
              ...column,
              [field]: value,
            }
          : column
      )
    );
  };

  // Auto-fill a GCC column's four index values from the StatsSA release for its
  // linked certificate month. Best-effort — see src/utils/statssaIndices.js.
  const handleGccAutoFill = async (column) => {
    const parsed = parseIndexMonth(column.certificateMonth || "") || parseIndexMonth(column.certificateDate || "");
    const setState = (patch) => setGccStatssa((prev) => ({ ...prev, [column.id]: { ...(prev[column.id] || {}), ...patch } }));
    if (!parsed) {
      setState({ busy: false, msg: { type: "error", text: 'Link a certificate month (e.g. "March 2026") to this column first.' } });
      return;
    }
    setState({ busy: true, msg: { type: "info", text: `Reading StatsSA releases for ${parsed.monthName} ${parsed.year}…` } });
    try {
      let result = await fetchStatssaIndices({ ym: parsed.ym, monthName: parsed.monthName, year: parsed.year });
      // Months cached before Table 6 extraction existed can't serve a non-default
      // material row — refresh the cache once to pick up the full table.
      if (materialSeriesKey !== MATERIAL_SERIES_DEFAULT_KEY && result.cached && (!result.materialTable6 || result.materialTable6[materialSeriesKey] == null)) {
        result = await fetchStatssaIndices({ ym: parsed.ym, monthName: parsed.monthName, year: parsed.year, force: true });
      }
      const { patch, filled, missing, provisional } = mapResultToIndexValues(GCC_CPA_BASE_INDEX_DEFINITION.rowIndexLabels, result, { materialSeriesKey });
      Object.entries(patch).forEach(([key, value]) => updateColumn(column.id, key, value));
      // Per-cell highlight state: true = amber (carried from earlier month, awaiting
      // final), false = green (final figures for this month). Merge onto the column.
      const mergedFlags = { ...(column.statssaProvisional || {}) };
      filled.forEach((key) => { mergedFlags[key] = provisional[key] === true; });
      updateColumn(column.id, "statssaProvisional", mergedFlags);

      const fLabels = filled.map((l) => STATSSA_SERIES_LABELS[l] || l);
      const mLabels = missing.map((l) => STATSSA_SERIES_LABELS[l] || l);
      const carried = filled.filter((l) => provisional[l]).map((l) => STATSSA_SERIES_LABELS[l] || l);
      const reason = result?.errors?.[0] ? ` ${result.errors[0]}` : "";
      let msg;
      if (filled.length && carried.length) {
        msg = { type: "warn", text: `Auto-filled from StatsSA. ${carried.join(", ")} not published for ${parsed.monthName} ${parsed.year} yet — carried forward from the previous month (amber) and awaiting final figures.${missing.length ? ` Couldn't read ${mLabels.join(", ")}.` : ""}` };
      } else if (filled.length && !missing.length) {
        msg = { type: "ok", text: `Auto-filled ${fLabels.join(", ")} from StatsSA ${parsed.monthName} ${parsed.year} (green = final). Verify against the release before finalising.` };
      } else if (filled.length) {
        msg = { type: "warn", text: `Filled ${fLabels.join(", ")}. Couldn't read ${mLabels.join(", ")} — enter those manually.${reason}` };
      } else {
        msg = { type: "warn", text: `No values found for ${parsed.monthName} ${parsed.year} — the StatsSA release may not be published yet, or its layout changed. Enter the values manually.${reason}` };
      }
      setState({ busy: false, msg });
    } catch (err) {
      setState({ busy: false, msg: { type: "error", text: err?.message || "Auto-fill failed — enter the values manually." } });
    }
  };

  // Auto-fill a base index set's four figures from the StatsSA release for its
  // base month (entered in "Month Indices Revised" — one month before tender close).
  const handleBaseAutoFill = async (set) => {
    const parsed = parseIndexMonth(set.revisedMonth || "");
    const setState = (patch) => setBaseStatssa((prev) => ({ ...prev, [set.id]: { ...(prev[set.id] || {}), ...patch } }));
    if (!parsed) {
      setState({ busy: false, msg: { type: "error", text: 'Enter the base month in "Month Indices Revised" (e.g. "May 2023") first.' } });
      return;
    }
    setState({ busy: true, msg: { type: "info", text: `Reading StatsSA releases for ${parsed.monthName} ${parsed.year}…` } });
    try {
      const result = await fetchStatssaIndices({ ym: parsed.ym, monthName: parsed.monthName, year: parsed.year });
      const { patch, filled, missing } = mapResultToIndexValues(GCC_CPA_BASE_INDEX_DEFINITION.rowIndexLabels, result, { materialSeriesKey });
      Object.entries(patch).forEach(([key, value]) => updateGccBaseIndexSetValue(set.id, key, value));
      const fLabels = filled.map((l) => STATSSA_SERIES_LABELS[l] || l);
      const mLabels = missing.map((l) => STATSSA_SERIES_LABELS[l] || l);
      const carried = filled.filter((l) => result?.provisional?.[l]).length;
      const reason = result?.errors?.[0] ? ` ${result.errors[0]}` : "";
      let msg;
      if (filled.length && !missing.length && !carried) {
        msg = { type: "ok", text: `Base figures filled from StatsSA ${parsed.monthName} ${parsed.year}: ${fLabels.join(", ")}. Verify against the release.` };
      } else if (filled.length) {
        msg = { type: "warn", text: `Filled ${fLabels.join(", ")}${carried ? " (some carried from an earlier month — check they're the base month's final figures)" : ""}.${missing.length ? ` Couldn't read ${mLabels.join(", ")} — enter manually.` : ""}${reason}` };
      } else {
        msg = { type: "warn", text: `No values found for ${parsed.monthName} ${parsed.year} — enter the base figures manually.${reason}` };
      }
      setState({ busy: false, msg });
    } catch (err) {
      setState({ busy: false, msg: { type: "error", text: err?.message || "Auto-fill failed — enter the base figures manually." } });
    }
  };

  const persistGccBaseIndexSets = (nextSets) => {
    const safeSets = nextSets.length ? nextSets : [createDefaultBaseIndexSet(GCC_CPA_BASE_INDEX_DEFINITION)];
    const fallbackSetId = safeSets[0]?.id || "base-set-1";

    setLocalGccBaseIndexSets(safeSets);

    if (!setCpaColumns) return;
    setCpaColumns((prev) =>
      (prev || []).map((column) => {
        const selectedSetStillExists = safeSets.some(
          (set) => String(set.id) === String(column.baseIndexSetId)
        );
        return {
          ...column,
          baseIndexSets: safeSets,
          baseIndexSetId: selectedSetStillExists ? column.baseIndexSetId : fallbackSetId,
        };
      })
    );
  };

  const updateGccBaseIndexSetLabel = (setId, value) => {
    persistGccBaseIndexSets(
      gccBaseIndexSets.map((set) => (set.id === setId ? { ...set, label: value } : set))
    );
  };

  const updateGccBaseIndexSetRevisedMonth = (setId, value) => {
    persistGccBaseIndexSets(
      gccBaseIndexSets.map((set) => (set.id === setId ? { ...set, revisedMonth: value } : set))
    );
  };

  const updateGccBaseIndexSetValue = (setId, indexKey, value) => {
    persistGccBaseIndexSets(
      gccBaseIndexSets.map((set) =>
        set.id === setId
          ? { ...set, baseIndices: { ...(set.baseIndices || {}), [indexKey]: value } }
          : set
      )
    );
  };

  const addGccBaseIndexSet = () => {
    persistGccBaseIndexSets([
      ...gccBaseIndexSets,
      createDefaultBaseIndexSet(
        GCC_CPA_BASE_INDEX_DEFINITION,
        gccBaseIndexSets.length,
        gccBaseIndexSets[gccBaseIndexSets.length - 1]?.baseIndices || {}
      ),
    ]);
  };

  const removeGccBaseIndexSet = (setId) => {
    if (gccBaseIndexSets.length <= 1) return;
    persistGccBaseIndexSets(gccBaseIndexSets.filter((set) => set.id !== setId));
  };

  const updateGccCertificateSelection = (columnId, selectedCertificateNo) => {
    const selectedCertificate = certificateOptions.find(
      (certificate) => certificate.value === selectedCertificateNo
    );

    // Cumulative escalatable base from the linked certificate (Phase 3). Auto-fills
    // "Total scheduled items" so CPA follows the certificate's cumulative CPA-subject
    // work. Any manual override already on the column is preserved.
    const linkedScheduled = selectedCertificate?.cpaScheduledItemsCumulative;

    if (!setCpaColumns) return;
    setCpaColumns((prev) =>
      (prev || []).map((column) =>
        column.id === columnId
          ? {
              ...column,
              certificateNo: selectedCertificateNo,
              certificateDate: selectedCertificate?.certificateDate || column.certificateDate || "",
              certificateMonth: selectedCertificate?.certificateMonth || column.certificateMonth || "",
              totalScheduledItems:
                linkedScheduled !== undefined && linkedScheduled !== "" && (column.totalScheduledItems === undefined || column.totalScheduledItems === "")
                  ? String(linkedScheduled)
                  : column.totalScheduledItems,
            }
          : column
      )
    );

    // Linking a certificate sets the processing month → auto-populate that
    // month's indices if the column doesn't have any yet.
    const linkedColumn = (cpaColumns || []).find((c) => c.id === columnId);
    const month = selectedCertificate?.certificateMonth || linkedColumn?.certificateMonth || "";
    const hasIndices = linkedColumn && STATSSA_SERIES_KEYS.some((k) => linkedColumn[k] !== undefined && String(linkedColumn[k]) !== "");
    if (month && linkedColumn && !hasIndices) {
      handleGccAutoFill({ ...linkedColumn, certificateNo: selectedCertificateNo, certificateMonth: month });
    }
  };

  const addColumn = () => {
    if (!setCpaColumns) return;
    setCpaColumns((prev) => {
      const columns = prev || [];
      const previousColumn = columns.length ? columns[columns.length - 1] : null;
      const baseSets = columns.length ? normaliseGccBaseIndexSets(columns) : gccBaseIndexSets;
      return [...columns, createEmptyCpaColumn(columns.length, previousColumn, baseSets)];
    });
  };

  const removeColumn = (columnId) => {
    if (!setCpaColumns) return;
    setCpaColumns((prev) => (prev || []).filter((column) => column.id !== columnId));
  };

  const updateFidicTable = (tableKey, patch) => {
    persistFidicTables((prev) => {
      const normalised = normaliseFidicCpaState(prev);
      return {
        ...normalised,
        [tableKey]: {
          ...normalised[tableKey],
          ...patch,
        },
      };
    });
  };

  const showToast = (message) => {
    setToast({ visible: true, message });
    setTimeout(() => setToast({ visible: false, message }), 2500);
  };

  // ─── CHANGE 1: addFidicRow passes the last existing row so indices are copied ───
  const addFidicRow = (tableDefinition) => {
    persistFidicTables((prev) => {
      const normalised = normaliseFidicCpaState(prev);
      const rows = normalised[tableDefinition.key]?.rows || [];
      // Grab the last row to copy its index values into the new row
      const lastRow = rows.length > 0 ? rows[rows.length - 1] : null;
      return {
        ...normalised,
        [tableDefinition.key]: {
          ...normalised[tableDefinition.key],
          rows: [...rows, createEmptyFidicRow(rows.length, tableDefinition, lastRow)],
        },
      };
    });
    showToast(`Monthly row added to ${tableDefinition.title} — indices copied from previous row (highlighted orange)`);
  };

  const removeFidicRow = (tableKey, rowId) => {
    persistFidicTables((prev) => {
      const normalised = normaliseFidicCpaState(prev);
      const rows = normalised[tableKey]?.rows || [];
      return {
        ...normalised,
        [tableKey]: {
          ...normalised[tableKey],
          rows: rows.filter((row) => row.id !== rowId),
        },
      };
    });
  };

  return (
    <section className="cpa-lux" style={SECTION_STYLES.page}>
      <style>{`
        .cpa-lux table { border-collapse: separate; border-spacing: 0; width: 100%; background: #fff; }
        .cpa-lux thead th { background: #f1f6fd; color: #64748b; font-weight: 800; font-size: 10.5px; text-transform: uppercase; letter-spacing: 0.05em; padding: 11px 14px; text-align: left; border-bottom: 1px solid #e2e8f0; white-space: nowrap; }
        .cpa-lux tbody td { padding: 8px 12px; border-bottom: 1px solid #eef2f7; color: #0f172a; font-size: 13px; vertical-align: middle; }
        .cpa-lux tbody tr:last-child td { border-bottom: none; }
        .cpa-lux tbody tr:hover td { background: #f9fbfe; }
        .cpa-lux tfoot td { padding: 10px 12px; font-weight: 800; background: #f1f6fd; border-top: 2px solid #e2e8f0; color: #0f172a; }
        .cpa-lux table input[type=text], .cpa-lux table input[type=number], .cpa-lux table select { width: 100%; box-sizing: border-box; border: 1px solid #dbe4ee; border-radius: 8px; padding: 7px 10px; font-size: 13px; font-weight: 600; color: #0f172a; background: #fff; transition: border-color .15s, box-shadow .15s; }
        .cpa-lux table input[type=number] { text-align: right; font-variant-numeric: tabular-nums; }
        .cpa-lux table input:focus, .cpa-lux table select:focus { outline: none; border-color: #2563eb; box-shadow: 0 0 0 3px rgba(37,99,235,0.13); }
        .cpa-lux table input::placeholder { color: #94a3b8; font-weight: 500; }
        .cpa-lux .cpa-kpi { position: relative; overflow: hidden; transition: transform .15s, box-shadow .15s; }
        .cpa-lux .cpa-kpi::before { content: ""; position: absolute; left: 0; top: 0; bottom: 0; width: 4px; background: linear-gradient(180deg, #2563eb, #38bdf8); }
        .cpa-lux .cpa-kpi:hover { transform: translateY(-2px); box-shadow: 0 12px 26px rgba(15,23,42,0.08); }
        .cpa-lux .cpa-substep { display: flex; align-items: center; gap: 10px; margin: 4px 0 12px; }
        .cpa-lux .cpa-substep-num { flex: none; width: 26px; height: 26px; border-radius: 8px; background: linear-gradient(135deg,#2563eb,#1d4ed8); color: #fff; font-weight: 800; font-size: 13px; display: inline-flex; align-items: center; justify-content: center; box-shadow: 0 6px 14px rgba(37,99,235,0.28); }
        .cpa-lux .cpa-substep-title { font-size: 15px; font-weight: 800; color: #0f172a; letter-spacing: -0.01em; }
        .cpa-lux .cpa-substep-hint { font-size: 12.5px; color: #64748b; }
      `}</style>
      <div style={SECTION_STYLES.sectionCard}>
        <div style={SECTION_STYLES.sectionHeader}>
          <div>
            <h2 style={SECTION_STYLES.sectionTitle}>Contract Price Adjustment</h2>
            <p style={SECTION_STYLES.sectionSubtitle}>
              Capture monthly StatsSA indices, and calculate the CPA that feeds your certificates and cashflow.
            </p>
          </div>
	          <div style={SECTION_STYLES.buttonRow}>
	            <span style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "#eff6ff", border: "1px solid #bfdbfe", color: "#1d4ed8", borderRadius: 9999, padding: "8px 16px", fontWeight: 800, fontSize: 13, letterSpacing: "0.02em" }}>
	              <span style={{ width: 8, height: 8, borderRadius: 9999, background: "#2563eb", boxShadow: "0 0 0 3px rgba(37,99,235,0.18)" }} />
	              {effectiveContractType} contract
	            </span>
	            {effectiveContractType === "GCC" ? (
              <button onClick={addColumn} style={SECTION_STYLES.primaryButton}>
                + Add CPA Column
              </button>
            ) : null}
          </div>
        </div>

        <div style={{ padding: "14px 18px", background: "#f8fbff", border: "1px solid #dbe7f5", borderRadius: 14, display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap" }}>
          <div>
            <p style={{ ...SECTION_STYLES.kpiLabel, marginBottom: 2 }}>CPA Snapshot</p>
            <p style={{ ...SECTION_STYLES.muted, marginTop: 0 }}>View cumulative CPA up to a specific certificate — updates all tables and tiles.</p>
          </div>
          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <select
              value={snapshotCertFilter}
              onChange={(e) => setSnapshotCertFilter(e.target.value)}
              style={{ ...SECTION_STYLES.secondaryButton, cursor: "pointer", minWidth: 240, fontWeight: 700 }}
            >
              <option value="">All certificates (latest totals)</option>
              {certificateOptions.map((cert) => (
                <option key={cert.value} value={cert.value}>{cert.label}</option>
              ))}
            </select>
            {snapshotCertFilter && (
              <button type="button" onClick={() => setSnapshotCertFilter("")} style={SECTION_STYLES.compactButton}>
                Clear
              </button>
            )}
            {snapshotCertFilter && (
              <span style={{ fontSize: 13, fontWeight: 700, color: "#1d4ed8" }}>
                Viewing up to cert {snapshotCertFilter}
              </span>
            )}
          </div>
        </div>

        <div style={SECTION_STYLES.kpiGrid}>
          <div className="cpa-kpi" style={SECTION_STYLES.kpiCard}>
            <p style={SECTION_STYLES.kpiLabel}>Contract Setup</p>
            <p style={SECTION_STYLES.kpiValue}>{effectiveContractType}</p>
          </div>
          <div className="cpa-kpi" style={SECTION_STYLES.kpiCard}>
            <p style={SECTION_STYLES.kpiLabel}>
              {snapshotCertFilter ? `Cumulative CPA — up to cert ${snapshotCertFilter}` : "Latest Cumulative CPA"}
            </p>
            <p style={{ ...SECTION_STYLES.kpiValue, fontSize: 24, color: "#1d4ed8" }}>
              {formatAmount(displayCumulativeCpa)}
            </p>
          </div>
          <div className="cpa-kpi" style={SECTION_STYLES.kpiCard}>
            <p style={SECTION_STYLES.kpiLabel}>{effectiveContractType === "FIDIC" ? "FIDIC Tables Enabled" : "GCC CPA Columns"}</p>
            <p style={SECTION_STYLES.kpiValue}>
              {effectiveContractType === "FIDIC"
                ? FIDIC_CPA_TABLES.filter((tableDefinition) => normalisedFidicTables[tableDefinition.key]?.enabled !== false).length
                : totalColumns}
            </p>
          </div>
        </div>
      </div>

	      {effectiveContractType === "GCC" ? (
	        <div style={SECTION_STYLES.sectionCard}>
	          <div style={SECTION_STYLES.sectionHeader}>
	            <div>
	              <h3 style={SECTION_STYLES.sectionTitle}>CPA Table</h3>
	              <p style={SECTION_STYLES.sectionSubtitle}>
	                Define GCC base indices, select the applicable base per certificate column, and feed cumulative CPA back into your certificate workflow.
	              </p>
	              <div style={{ display: "flex", gap: 16, alignItems: "center", fontSize: 12, fontWeight: 600, color: "#475569", flexWrap: "wrap", marginTop: 8 }}>
	                <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
	                  <span style={{ width: 14, height: 14, borderRadius: 4, background: "#f0fdf4", border: "1.5px solid #22c55e", display: "inline-block" }} />
	                  Final figures (published for this month)
	                </span>
	                <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
	                  <span style={{ width: 14, height: 14, borderRadius: 4, background: "#fff7ed", border: "1.5px solid #f97316", display: "inline-block" }} />
	                  Provisional — carried from previous month, awaiting final figures
	                </span>
	              </div>
	            </div>
	            <div style={{ minWidth: 280 }}>
	              <label style={{ display: "block", fontSize: 11, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.06em", color: "#64748b", marginBottom: 6 }}>
	                Material index — P0151.1 Table 6 row
	              </label>
	              <select
	                value={materialSeriesKey}
	                onChange={(e) => persistMaterialSeries(e.target.value)}
	                title="Which Table 6 row this project's material escalation reads. Applies to every auto-fill on this project."
	                style={{ width: "100%", border: "1px solid #dbe4ee", borderRadius: 10, padding: "9px 12px", fontSize: 13, fontWeight: 700, color: "#0f172a", background: "#fff" }}
	              >
	                {MATERIAL_SERIES_OPTIONS.map((o) => (
	                  <option key={o.key} value={o.key}>{o.label}</option>
	                ))}
	              </select>
	              <p style={{ margin: "6px 0 0", fontSize: 11, color: "#94a3b8", lineHeight: 1.4 }}>
	                Table 6 has multiple material rows — pick the one that matches this project's works. Saved per project.
	              </p>
	            </div>
	          </div>

	          <div className="cpa-substep">
	            <span className="cpa-substep-num">1</span>
	            <div>
	              <div className="cpa-substep-title">Base Indices</div>
	              <div className="cpa-substep-hint">Set the base month (a month before tender close) and its published indices — use Auto-fill to pull them from StatsSA.</div>
	            </div>
	          </div>
	          <div style={SECTION_STYLES.tableWrap}>
	            <table>
	              <thead>
	                <tr>
	                  <th>Base Set</th>
	                  <th>Month Indices Revised</th>
	                  {GCC_CPA_BASE_INDEX_DEFINITION.rowIndexLabels.map((label) => (
	                    <th key={label}>{GCC_CPA_BASE_INDEX_DEFINITION.baseIndexLabels[label]}</th>
	                  ))}
	                  <th>Actions</th>
	                </tr>
	              </thead>
	              <tbody>
	                {gccBaseIndexSets.map((set, index) => (
	                  <tr key={set.id}>
	                    <td style={SECTION_STYLES.rowLabel}>
	                      <input
	                        type="text"
	                        value={set.label || `Base Set ${index + 1}`}
	                        onChange={(e) => updateGccBaseIndexSetLabel(set.id, e.target.value)}
	                      />
	                    </td>
	                    <td>
	                      <input
	                        type="text"
	                        placeholder="e.g. May 2023"
	                        title="Base month — normally the month before tender close. Auto-fill reads the base figures from the StatsSA release for this month."
	                        value={set.revisedMonth || ""}
	                        onChange={(e) => updateGccBaseIndexSetRevisedMonth(set.id, e.target.value)}
	                      />
	                    </td>
	                    {GCC_CPA_BASE_INDEX_DEFINITION.rowIndexLabels.map((label) => (
	                      <td key={`${set.id}-${label}`}>
	                        <input
	                          type="number"
	                          step="any"
	                          value={set.baseIndices?.[label] ?? ""}
	                          onChange={(e) => updateGccBaseIndexSetValue(set.id, label, e.target.value)}
	                        />
	                      </td>
	                    ))}
	                    <td>
	                      <div style={{ display: "grid", gap: 6, justifyItems: "start" }}>
	                        <button
	                          type="button"
	                          onClick={() => handleBaseAutoFill(set)}
	                          disabled={baseStatssa[set.id]?.busy}
	                          title="Read the base labour/plant/material/fuel figures from the StatsSA release for this set's base month"
	                          style={{
	                            display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 10px", borderRadius: 8,
	                            border: "1px solid #2563eb", background: baseStatssa[set.id]?.busy ? "#eff6ff" : "#fff",
	                            color: "#1d4ed8", fontWeight: 700, fontSize: 12, cursor: baseStatssa[set.id]?.busy ? "default" : "pointer", whiteSpace: "nowrap",
	                          }}
	                        >{baseStatssa[set.id]?.busy ? "Reading StatsSA…" : "⤓ Auto-fill from StatsSA"}</button>
	                        <button
	                          type="button"
	                          onClick={() => removeGccBaseIndexSet(set.id)}
	                          style={SECTION_STYLES.dangerButton}
	                          disabled={gccBaseIndexSets.length <= 1}
	                        >
	                          Delete
	                        </button>
	                        {baseStatssa[set.id]?.msg && (
	                          <div style={{
	                            padding: "6px 8px", borderRadius: 6, fontSize: 11, fontWeight: 600, lineHeight: 1.35, maxWidth: 260,
	                            ...(baseStatssa[set.id].msg.type === "ok"    ? { background: "#f0fdf4", border: "1px solid #86efac", color: "#14532d" } : {}),
	                            ...(baseStatssa[set.id].msg.type === "warn"  ? { background: "#fff7ed", border: "1px solid #fdba74", color: "#92400e" } : {}),
	                            ...(baseStatssa[set.id].msg.type === "error" ? { background: "#fef2f2", border: "1px solid #fca5a5", color: "#991b1b" } : {}),
	                            ...(baseStatssa[set.id].msg.type === "info"  ? { background: "#eff6ff", border: "1px solid #bfdbfe", color: "#1e40af" } : {}),
	                          }}>{baseStatssa[set.id].msg.text}</div>
	                        )}
	                      </div>
	                    </td>
	                  </tr>
	                ))}
	                <tr>
	                  <td colSpan={3 + GCC_CPA_BASE_INDEX_DEFINITION.rowIndexLabels.length}>
	                    <button type="button" onClick={addGccBaseIndexSet} style={SECTION_STYLES.secondaryButton}>
	                      Add Base Index Set
	                    </button>
	                  </td>
	                </tr>
	              </tbody>
	            </table>
	          </div>

	          <div className="cpa-substep" style={{ marginTop: 20 }}>
	            <span className="cpa-substep-num">2</span>
	            <div>
	              <div className="cpa-substep-title">Monthly CPA Columns</div>
	              <div className="cpa-substep-hint">One column per certificate month. Link a certificate, Auto-fill the monthly indices, and the CPA is calculated automatically.</div>
	            </div>
	          </div>
	          <div style={SECTION_STYLES.tableWrap}>
	            <table>
              <thead>
                <tr>
                  <th style={SECTION_STYLES.stickyCell}>Column A Heading</th>
                  {filteredGccColumns.length === 0 ? (
                    <th>No columns match the current filter</th>
                  ) : (
                    filteredGccColumns.map((column, index) => (
                      <th key={column.id} style={{ minWidth: 220, verticalAlign: "top" }}>
                        <div style={{ display: "grid", gap: 10 }}>
                          <strong>Column {index + 1}</strong>
                          <div style={SECTION_STYLES.muted}>
                            {column.linkedCertificate ? "Linked to issued certificate" : "Not linked to issued certificate"}
                          </div>
                          {certificateOptions.length ? (
                            <select
                              value={column.certificateNo || ""}
                              onChange={(e) => updateGccCertificateSelection(column.id, e.target.value)}
                            >
                              <option value="">Link certificate</option>
                              {certificateOptions.map((certificate) => (
                                <option key={certificate.value} value={certificate.value}>
                                  {certificate.label}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <input
                              type="text"
                              placeholder="Certificate no"
                              value={column.certificateNo || ""}
                              onChange={(e) => updateColumn(column.id, "certificateNo", e.target.value)}
                            />
                          )}
	                          <select
	                            value={column.indexStatus || "UL"}
	                            onChange={(e) => updateColumn(column.id, "indexStatus", e.target.value)}
	                          >
	                            <option value="UL">Provisional indices</option>
	                            <option value="L">Final indices</option>
	                          </select>
	                          <select
	                            value={column.baseIndexSetId || gccBaseIndexSets[0]?.id || "base-set-1"}
	                            onChange={(e) => updateColumn(column.id, "baseIndexSetId", e.target.value)}
	                          >
	                            {gccBaseIndexSets.map((set, baseIndex) => (
	                              <option key={set.id} value={set.id}>
	                                {set.label || `Base Set ${baseIndex + 1}`}
	                                {set.revisedMonth ? ` — ${set.revisedMonth}` : ""}
	                              </option>
	                            ))}
	                          </select>
	                          <input
	                            type="date"
	                            value={column.certificateDate || ""}
	                            onChange={(e) => updateColumn(column.id, "certificateDate", e.target.value)}
	                          />
                          <select
                            value={column.certificateMonth || ""}
                            title="Month of processing — the system reads the StatsSA indices for this month"
                            onChange={(e) => {
                              const value = e.target.value;
                              updateColumn(column.id, "certificateMonth", value);
                              // Auto-populate the month's indices as soon as the
                              // processing month is chosen (cache-first; skips if
                              // the column already has index values).
                              const hasIndices = STATSSA_SERIES_KEYS.some((k) => column[k] !== undefined && String(column[k]) !== "");
                              if (value && !hasIndices) handleGccAutoFill({ ...column, certificateMonth: value });
                            }}
                          >
                            <option value="">Month of processing</option>
                            {availableCertificateMonths.map((month) => (
                              <option key={getMonthValue(month)} value={getMonthValue(month)}>
                                {getMonthLabel(month)}
                              </option>
                            ))}
                          </select>
	                          <input
	                            type="text"
	                            placeholder="Period no"
                            value={column.periodNo || ""}
                            onChange={(e) => updateColumn(column.id, "periodNo", e.target.value)}
                          />
                          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                            <button
                              onClick={() =>
                                handleViewItem && handleViewItem(`CPA ${column.certificateMonth || `Column ${index + 1}`}`, column)
                              }
                              style={SECTION_STYLES.compactButton}
                            >
                              View
                            </button>
                            <button onClick={() => removeColumn(column.id)} style={SECTION_STYLES.dangerButton}>
                              Delete
                            </button>
                          </div>
                          {(() => {
                            const st = gccStatssa[column.id] || {};
                            return (
                              <div style={{ display: "grid", gap: 6 }}>
                                <button
                                  type="button"
                                  onClick={() => handleGccAutoFill(column)}
                                  disabled={st.busy}
                                  title="Read labour, plant, material and fuel indices from the StatsSA release for this column's certificate month"
                                  style={{
                                    display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6,
                                    padding: "6px 10px", borderRadius: 8, border: "1px solid #2563eb",
                                    background: st.busy ? "#eff6ff" : "#fff", color: "#1d4ed8", fontWeight: 700,
                                    fontSize: 12, cursor: st.busy ? "default" : "pointer",
                                  }}
                                >{st.busy ? "Reading StatsSA…" : "⤓ Auto-fill from StatsSA"}</button>
                                {st.msg && (
                                  <div style={{
                                    padding: "6px 8px", borderRadius: 6, fontSize: 11, fontWeight: 600, lineHeight: 1.35,
                                    ...(st.msg.type === "ok"    ? { background: "#f0fdf4", border: "1px solid #86efac", color: "#14532d" } : {}),
                                    ...(st.msg.type === "warn"  ? { background: "#fff7ed", border: "1px solid #fdba74", color: "#92400e" } : {}),
                                    ...(st.msg.type === "error" ? { background: "#fef2f2", border: "1px solid #fca5a5", color: "#991b1b" } : {}),
                                    ...(st.msg.type === "info"  ? { background: "#eff6ff", border: "1px solid #bfdbfe", color: "#1e40af" } : {}),
                                  }}>{st.msg.text}</div>
                                )}
                              </div>
                            );
                          })()}
                        </div>
                      </th>
                    ))
                  )}
                </tr>
              </thead>
              <tbody>
                {CPA_ROW_DEFINITIONS.map((row) => (
                  <tr key={row.key}>
                    <td style={SECTION_STYLES.stickyCell}>
                      <div style={SECTION_STYLES.rowLabel}>{row.label}</div>
                      {row.highlight ? (
                        <div style={SECTION_STYLES.muted}>Key value used by the app</div>
                      ) : null}
                    </td>
                    {filteredGccColumns.length === 0 ? (
                      <td style={{ color: "#64748b" }}>No columns match the current filter.</td>
                    ) : (
                      filteredGccColumns.map((column) => {
                        const rawValue =
                          row.type === "computed"
                            ? getValueFromSource(column, row.source)
                            : column[row.key];

                        const displayValue =
                          row.kind === "amount"
                            ? formatAmount(rawValue)
                            : row.kind === "index" && row.type === "computed"
                            ? formatIndex(rawValue)
                            : rawValue;

                        return (
                          <td key={`${column.id}-${row.key}`}>
                            {row.type === "input" ? (
                              <input
                                style={{
                                  ...SECTION_STYLES.inputCell,
                                  ...(row.kind === "index" && column.statssaProvisional?.[row.key] === true
                                    ? { background: "#fff7ed", borderColor: "#f97316", color: "#92400e", fontWeight: 700 }
                                    : row.kind === "index" && column.statssaProvisional?.[row.key] === false
                                    ? { background: "#f0fdf4", borderColor: "#22c55e", color: "#14532d", fontWeight: 700 }
                                    : {}),
                                }}
                                type={row.kind === "quantity" || row.kind === "index" || row.kind === "amount" ? "number" : "text"}
                                step="any"
                                value={rawValue ?? ""}
                                onChange={(e) => {
                                  updateColumn(column.id, row.key, e.target.value);
                                  // A manual edit to an auto-filled index clears its highlight.
                                  if (row.kind === "index" && column.statssaProvisional?.[row.key] !== undefined) {
                                    updateColumn(column.id, "statssaProvisional", { ...column.statssaProvisional, [row.key]: undefined });
                                  }
                                }}
                              />
                            ) : (
                              <input
                                style={{
                                  ...SECTION_STYLES.readonlyCell,
                                  fontWeight: row.highlight ? 800 : 700,
                                  color: row.highlight ? "#1d4ed8" : "#0f172a",
                                }}
                                type="text"
                                value={displayValue}
                                readOnly
                              />
                            )}
                          </td>
                        );
                      })
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <>
          {/* FIDIC Summary KPI tiles — above audit, update per snapshot */}
          <div style={SECTION_STYLES.sectionCard}>
            <div style={SECTION_STYLES.sectionHeader}>
              <div>
                <h3 style={SECTION_STYLES.sectionTitle}>FIDIC CPA Summary</h3>
                <p style={SECTION_STYLES.sectionSubtitle}>
                  {snapshotCertFilter
                    ? `Showing cumulative escalation up to and including certificate ${snapshotCertFilter}.`
                    : "Select the applicable FIDIC escalation tables for this project. The combined total below is the value intended to feed the Certificates CPA column and the Cashflow Actual CPA value."}
                </p>
              </div>
            </div>
            <div style={SECTION_STYLES.kpiGrid}>
              {FIDIC_CPA_TABLES.map((tableDefinition) => (
                <div key={tableDefinition.key} style={SECTION_STYLES.kpiCard}>
                  <p style={SECTION_STYLES.kpiLabel}>{tableDefinition.title}</p>
                  <p style={SECTION_STYLES.kpiValue}>
                    {formatAmount(
                      normalisedFidicTables[tableDefinition.key]?.enabled === false
                        ? 0
                        : filteredFidicTableTotals
                          ? filteredFidicTableTotals[tableDefinition.key]
                          : fidicTotals.computedTables[tableDefinition.key]?.totalEscalation
                    )}
                  </p>
                </div>
              ))}
              <div style={{ ...SECTION_STYLES.kpiCard, background: "#f8fbff" }}>
                <p style={SECTION_STYLES.kpiLabel}>
                  {snapshotCertFilter ? `Combined CPA — up to cert ${snapshotCertFilter}` : "Combined Total CPA"}
                </p>
                <p style={{ ...SECTION_STYLES.kpiValue, color: "#1d4ed8" }}>
                  {formatAmount(snapshotCertFilter ? filteredFidicCombinedCpa : fidicTotals.combinedTotalCpa)}
                </p>
              </div>
            </div>
          </div>

          <CollapsibleAuditSection
            title="FIDIC CPA Adjustment Audit"
            subtitle="This groups all enabled FIDIC CPA table rows by certificate number. Issued certificates remain locked, while revised/final indices carry forward through the latest cumulative CPA used by later certificates."
            defaultOpen={false}
          >
            <div style={SECTION_STYLES.tableWrap}>
              <table>
                <thead>
                  <tr>
                    <th>Cert No</th>
                    <th>Status</th>
                    <th>Revised CPA This Cert</th>
                    <th>Issued / Locked CPA This Cert</th>
                    <th>Adjustment vs Issued</th>
                    <th>Revised Cumulative CPA</th>
                    <th>Issued / Locked Cumulative CPA</th>
                    <th>Cumulative Adjustment</th>
                  </tr>
                </thead>
                <tbody>
                  {fidicCertificateAuditRows.length === 0 ? (
                    <tr>
                      <td colSpan="8" style={{ color: "#64748b" }}>No FIDIC CPA rows captured yet.</td>
                    </tr>
                  ) : (
                    fidicCertificateAuditRows.map((row) => (
                      <tr key={row.certificateNo}>
                        <td>{row.certificateNo}</td>
                        <td>{row.linkedCertificate ? "Linked / issued" : "Not issued yet"}</td>
                        <td>{formatAmount(row.revisedCpaThisCertificate)}</td>
                        <td>{formatAmount(row.issuedLockedCpaThisCertificate)}</td>
                        <td style={{ fontWeight: 800, color: "#1d4ed8" }}>{formatAmount(row.adjustmentFromIssuedCpa)}</td>
                        <td>{formatAmount(row.revisedCpaCumulative)}</td>
                        <td>{formatAmount(row.issuedLockedCpaCumulative)}</td>
                        <td style={{ fontWeight: 800, color: "#1d4ed8" }}>{formatAmount(row.cumulativeAdjustmentFromIssued)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </CollapsibleAuditSection>

          {FIDIC_CPA_TABLES.map((tableDefinition) => (
            <FidicCpaTable
              key={tableDefinition.key}
              tableDefinition={tableDefinition}
              tableState={normalisedFidicTables[tableDefinition.key]}
              updateTable={updateFidicTable}
              removeRow={removeFidicRow}
              storedCertificates={storedCertificates}
              storedCertificateByNo={storedCertificateByNo}
              soqItems={soqItems}
              filterUpToCert={snapshotCertFilter}
            />
          ))}
        </>
      )}
      <Toast message={toast.message} visible={toast.visible} />
    </section>
  );
}
